import useNovelNovemberSprint from "../../hooks/useNovelNovemberSprint";
import { getUserId } from "../../lib/effectiveAuthUser";
import { syncAppliedAgentEdit } from "src/lib/syncAppliedAgentEdit";
import useWriteModel from "../../hooks/useWriteModel";
// Canonical function implementation for TiptapDocumentSB.
import { BENCHMARK_WRITE_MODEL, resolveBenchmarkWriteModel } from "../BenchmarkTakeover/proseMode";
import * as Sentry from "@sentry/react";
import { Fragment, Slice } from "prosemirror-model";
import { undoDepth, redoDepth } from "prosemirror-history";
import { TextSelection } from "prosemirror-state";
import React from "react";

import classnames from "classnames";
import { debounce } from "lodash";
import { Section } from "react-simple-resizer";
import { customFunctions } from "../../../../src/shared/customFunctions";
import Card from "../../models/Card";
import FavoritesStore from "../../models/FavoritesStore";
import PageNotFound from "../../PageNotFound";
import { SAMPLE_STORY, countWords, delimited, makeId, sendAppEvent, splitWords } from "../../Utils.js";
import { commentsManager } from "../ShareLinkPage/CommentsManager";
import BenchmarkTakeover from "../BenchmarkTakeover";

import {
  DEFAULT_ENGINE_SETTINGS,
  DEFAULT_WRITE_MODE,
  MAX_QUICK_EDIT_HIGHLIGHTED_WORDS,
  MAX_REWRITE_WORDS,
  MIN_DOC_TITLING_WORDS,
  MIN_REWRITE_WORDS,
  MIN_VISUALIZE_WORDS,
  WRITE_MODE_GUIDED,
  WRITE_MODE_TONE,
} from "../../../../src/shared/constants";
import { BLOCK_TYPES, PluginException } from "../../../../src/shared/schemas/PluginSchema";
import Auth from "../../Auth";
import Editor from "../../lib/Editor";
import { getEffectiveSelection } from "../../lib/tipTapExtensions/persistedSelection";
import { stripMarkdownForHighlightSnippet } from "~shared/agentHighlightSnippet";
import {
  collectInsertedMarkRanges,
  docRangeHasInsertedMark,
  expandRangeToInsertedMarkSpan,
  findAgentEditHighlightRange,
} from "../../../../src/lib/agentManuscriptHighlight";
import { applyAgentManuscriptReplacementInEditor, isAgentManuscriptReplacementSyncedInEditor } from "src/lib/applyAgentManuscriptReplacement";
import {
  applyAgentManuscriptUndoSnapshotToCollaborationYdoc,
  editorDocDiffersFromUndoSnapshot,
  editorDocMatchesUndoSnapshot,
  resolveCollaborationYdoc,
  serializeEditorDocJson,
  undoSnapshotMatchesCurrentEditorDoc,
  waitForEditorDocToMatchUndoSnapshot,
} from "src/lib/agentManuscriptEditUndo";
import { ySyncPluginKey } from "y-prosemirror";
import { CONTENT_POLICY_MESSAGE, ContentViolationError } from "../../lib/Errors";
import { convertQuotes, convertQuotesInHtml } from "../../lib/convertQuotes";
import { buildDraftListAppendTransaction, terminalDraftListEnd } from "../../lib/draftInsertion";
import { insertCardHtml } from "../../lib/insertCardHtml";
import { buildPmInlineNodesFromLines } from "../../lib/tiptapInlineBuilders";
import { getQuickEditCTAUpdates, getCommanderCTAUpdate } from "./newDocumentCTALifecycle";
import UserData from "../../models/UserData";
import { withAppStore } from "../../stores/appStore";
import { withCommentStore } from "../../stores/commentStore.js";
import { useProjectStore, withProjectStore } from "../../stores/projectStore";
import { snapshotClientWritePovTense, snapshotPovTenseSettings } from "../../../../src/shared/writePovTenseDiagnostics";
import { withStoryBibleStore, useStoryBibleStore } from "../../stores/storyBibleStore";
import { usePersistentChatStore } from "../../stores/persistentChatStore";
import { withStickyTourStore } from "../../stores/stickyTourStore";
import { maybeStartParagraphSpacingTour } from "../StickyTour/Tours/ParagraphSpacingTour";
import { TourSignal } from "../StickyTour/TourTypes";
import { withSudowriteStore } from "../../stores/sudowriteStore";
import { withCommentToggle } from "src/hooks/withCommentToggle";
import { CrossAppSignal, useCrossAppSignalStore } from "../../stores/crossAppSignalStore";
import FeedbackModal from "../Modals/FeedbackModal/index.js";
import RightBarContainer from "../RightBarContainer";
import StoryBible from "../StoryBible";
import CommandMenu from "./CommandMenu";
import DocumentStatusBanners from "./DocumentStatusBanners";
import EditorContentPanels from "./EditorContentPanels";
import { FindAndReplace } from "./FindAndReplace/";
import HighlightMenu from "./HighlightMenu";
import { SearchableProvider } from "../../lib/searchable";
import RelatedWordsStore from "./RelatedWordsStore";
import { ResizeHandle } from "./ResizeHandle";
import { StyledContainer } from "./styles";
import TiptapSearchableRegistrar from "./TiptapSearchableRegistrar";
import { getSelectedParagraph, getSelectedTextPixelPosition, replaceSelection, splitSingleNewlines } from "./Utils";
import { useRightBarCards } from "./useRightBarCards";
import { useStableCallback } from "./useStableCallback";
import {
  shouldCaptureAgentEditUndoSnapshot,
  shouldUseNativeUndo,
  shouldUseNativeRedo,
  buildRedoEntries,
  rebaseRedoEntries,
  extractTextFromDocJson,
} from "./agentEditUndoPriority";
import {
  applyEditorComputedStyles,
  applyFindAndReplaceOpenState,
  getBackwardTokenWindow,
  getBackwardWordWindow,
  getCenterColumnResizeState,
  getClearedSelectionState,
  getCollapsedSelectionState,
  getColumnResizeState,
  getDescriptionConsiderationFromText,
  getEditorComputedStyles,
  getExpandTokenWindow,
  getFeatureActionBlockReason,
  getForwardWordWindow,
  getIsUserInStoryBible,
  getStoryBibleDocumentLengthForContentState,
  getHighlightedWordCountState,
  getSelectionActionState,
  getTextChangeProjectUiState,
  getTextChangeResetState,
  getWindowResizeState,
  getWordCountFromText,
  isProgrammaticScrollAtBottom,
  shouldHandleTiptapSelectionUpdate,
  shouldShowHighlightMenu,
  shouldShowLinkingMenuOnEditorSynced,
} from "./helpers";
import { shouldSuppressEntityNavigationScrollReset } from "../PersistentChat/mentions/entityNavigationState";

const DEFAULT_CARET_POSITION = { x: -99999, y: -99999 };

const getDefaultCaretPosition = () => ({ ...DEFAULT_CARET_POSITION });

const getInitialTiptapDocumentSBState = () => ({
  notFound: false,
  engine: DEFAULT_ENGINE_SETTINGS.engine,
  stop: null,
  loading: 0,
  words: -1,
  wordCount: null,
  highlightedWordCount: null,
  line: null,
  message: "Loading...",
  error: null,
  existingCardsLoaded: false,
  selection: null,
  selectionRange: null,
  range: null,
  max_tokens: null,
  loadError: null,
  n: null,
  focus: false,

  expandSelection: false,
  rewriteSelection: false,
  describeSelection: false,
  commandMenuSelection: false,
  visualizeSelection: false,
  relatedClusters: null,

  labsOpen: false,
  storyToolOpen: false,

  toneOpen: false,
  writeOpen: false,
  describeOpen: false,
  guidances: null,
  loadingGuidances: false,
  guidanceOpen: false,
  modalOpen: false,

  menuHeight: 0,
  innerHeight: 0,

  newCards: [],
  existingCards: [],
  favorites: [],
  favoritesByCards: {},
  rightTab: 0,

  brainstormOpen: false,
  forceNeedsFeedback: false,
  showRelatedWords: false,

  seenTips: [],

  caretPosition: getDefaultCaretPosition(),
  commandModeInserting: false,
  commandModeState: "off",
  magicCursorVisible: false,
  escapeCommandMode: false,
  showThumbs: false,

  showCommandMenu: false,
  findAndReplaceOpen: false,

  editorReady: false,
  documentReady: false,

  shouldAutoScroll: true,
});

const getDocumentSwitchStateReset = () => ({
  editorReady: false,
  documentReady: false,
  message: "Loading...",
  newCards: [],
  existingCards: [],
  existingCardsLoaded: false,
  favorites: [],
  favoritesByCards: {},
  error: null,
  selection: null,
  selectionRange: null,
  range: null,
  wordCount: null,
  highlightedWordCount: null,
  expandSelection: false,
  rewriteSelection: false,
  describeSelection: false,
  commandMenuSelection: false,
  visualizeSelection: false,
  relatedClusters: null,
  guidances: null,
  loadingGuidances: false,
  notFound: false,
  loadError: null,
  loading: 0,
  caretPosition: getDefaultCaretPosition(),
  commandModeInserting: false,
  commandModeState: "off",
  magicCursorVisible: false,
  escapeCommandMode: false,
  showThumbs: false,
  showRelatedWords: false,
  brainstormOpen: false,
});

const getSetupEditorStateReset = () => ({
  editorActive: false,
  message: "Loading...",
  newCards: [],
  existingCards: [],
  favorites: [],
  favoritesByCards: {},
  error: null,
  selection: null,
  selectionRange: null,
  focus: false,
  labsOpen: false,
  storyToolOpen: false,
  loadingGuidances: false,
  guidanceOpen: false,
  toneOpen: false,
  writeOpen: false,
  describeOpen: false,
  modalOpen: false,
});

function TiptapDocumentSB(props) {
  const selectedWriteModel = useWriteModel({ editorOptions: props.editorOptions, initialWriteModel: props.writeModel });
  const sprint = useNovelNovemberSprint();
  const sprintRef = React.useRef(sprint);
  sprintRef.current = sprint;
  const [state, setState] = React.useState(() => getInitialTiptapDocumentSBState());
  const propsRef = React.useRef(props);
  const stateRef = React.useRef(state);
  const pendingSetStateCallbacksRef = React.useRef([]);
  const previousPropsRef = React.useRef(props);
  const previousStateRef = React.useRef(state);
  const isMountedRef = React.useRef(false);
  const didCommitRef = React.useRef(false);

  const outerScrollRef = React.useRef(null);
  const editorRef = React.useRef(null);
  const editorEndRef = React.useRef(null);
  const editorToolbarRef = React.useRef(null);
  const centerColumnRef = React.useRef(null);
  const rightBarRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const rootRef = React.useRef(null);
  const titleEditorRef = React.useRef(null);
  const pointerDownPosition = React.useRef(null);
  const pointerUpPosition = React.useRef(null);
  const [isPointerDragging, setIsPointerDragging] = React.useState(false);
  const commandMenuDidFinalizeChangesRef = React.useRef(false);

  const stride = 10;
  const lastKnownCursorPositionRef = React.useRef(null);
  const agentManuscriptWindowListenersBoundRef = React.useRef(false);
  const controllerRef = React.useRef(null);
  const documentSwitchVersionRef = React.useRef(0);
  const agentEditUndoStackRef = React.useRef([]);
  const agentEditRedoStackRef = React.useRef([]);
  const pendingAgentEditUndoSnapshotRef = React.useRef(null);
  const pendingAgentEditUndoDepthRef = React.useRef(null);
  const nativeUndoRedoSnapshotsRef = React.useRef([]);
  const editorHistoryMutationRef = React.useRef(false);
  const agentEditUndoCapturedRef = React.useRef(false);
  const containerLookupRetriesRef = React.useRef(0);
  const containerLookupRetryTimeoutRef = React.useRef(null);
  const placeholderRafIdRef = React.useRef(null);
  const setupEditorStartedRef = React.useRef(false);
  const componentUnmountedRef = React.useRef(false);
  const editorModelRef = React.useRef(null);
  const tiptapRef = React.useRef(null);
  const documentModelRef = React.useRef(null);
  const documentIdRef = React.useRef(null);
  const relatedWordsStoreRef = React.useRef(null);
  const favoritesStoreRef = React.useRef(null);
  const cardQueriesRef = React.useRef({});
  const editorContainerRef = React.useRef(null);
  const loadingDocumentRef = React.useRef(false);
  const disableInsertUnmarkingRef = React.useRef(false);
  const submittedRef = React.useRef(false);
  const considerationRef = React.useRef(null);
  const startRef = React.useRef(null);
  const errorTimeoutRef = React.useRef(null);
  const messageTimeoutRef = React.useRef(null);
  const commandModeIntervalRef = React.useRef(null);
  const guidanceAbortControllerRef = React.useRef(null);
  const onAgentYSyncTransactionRef = React.useRef(null);
  const onAgentHighlightDismissRef = React.useRef(null);
  const onAgentEditDocumentStartRef = React.useRef(null);
  const onAgentEditDocumentRecordUndoRef = React.useRef(null);
  const onAgentManuscriptUndoRef = React.useRef(null);
  const onAgentHighlightEditRef = React.useRef(null);
  const onAgentApplyManuscriptReplacementRef = React.useRef(null);
  const handleEditorClickRef = React.useRef(null);
  const handleEditorMousedownRef = React.useRef(null);
  const handlePointerDownRef = React.useRef(null);
  const handlePointerUpRef = React.useRef(null);
  const handlePointerDragEndRef = React.useRef(null);
  const agentEditSavedScrollTopRef = React.useRef(null);
  const agentEditSavedInnerScrollTopRef = React.useRef(null);
  const agentEditSavedIframeScrollTopRef = React.useRef(null);
  const agentEditScrollPreserveUntilRef = React.useRef(null);
  const lastRewriteModifierRef = React.useRef(null);

  // Mirror class-component `this.props` / `this.state` semantics for callbacks
  // that can run during the same layout-effect flush as a render. Waiting for a
  // separate layout effect leaves those callbacks with the previous commit's
  // values.
  propsRef.current = props;
  stateRef.current = state;

  if (!controllerRef.current) {
    controllerRef.current = new AbortController();
  }

  React.useEffect(() => {
    Sentry.setTag("tiptapDocumentSB.variant", "function");
    Sentry.setContext("tiptapDocumentSB", {
      implementation: "function",
      cutover: true,
      documentId: props.documentId || props.document?.id || null,
      projectId: props.project?.id || null,
      userId: props.user?.uid || null,
    });
  }, [props.document?.id, props.documentId, props.project?.id, props.user?.uid]);

  const setMergedState = React.useCallback((stateUpdate, callback) => {
    if (!isMountedRef.current) {
      return;
    }

    if (typeof callback === "function") {
      pendingSetStateCallbacksRef.current.push(callback);
    }

    setState((previousState) => {
      const partialState = typeof stateUpdate === "function" ? stateUpdate(previousState, propsRef.current) : stateUpdate;

      if (partialState === null || partialState === undefined) {
        return previousState;
      }

      return {
        ...previousState,
        ...partialState,
      };
    });
  }, []);

  // Editor.init() awaits IndexedDB/provider setup. During that wait, a document switch
  // or unmount can destroy the editor and start another one. Only the editor instance
  // that is still current for the captured switch version is allowed to finish setup.
  const isCurrentEditorSetup = React.useCallback((editorModel, switchVersion) => {
    const tiptap = editorModel?.tiptap;
    return (
      !componentUnmountedRef.current &&
      documentSwitchVersionRef.current === switchVersion &&
      editorModelRef.current === editorModel &&
      Boolean(tiptap) &&
      !editorModel?._destroyed &&
      !tiptap?.isDestroyed
    );
  }, []);

  const addStaleEditorSetupBreadcrumb = React.useCallback((phase, editorModel, switchVersion) => {
    Sentry.addBreadcrumb({
      category: "tiptapdocumentsb",
      level: "info",
      message: "Skipped stale Tiptap editor setup",
      data: {
        phase,
        documentId: documentIdRef.current,
        setupSwitchVersion: switchVersion,
        currentSwitchVersion: documentSwitchVersionRef.current,
        componentUnmounted: componentUnmountedRef.current,
        wasCurrentEditor: editorModelRef.current === editorModel,
        hasTiptap: Boolean(editorModel?.tiptap),
        editorDestroyed: Boolean(editorModel?._destroyed),
        tiptapDestroyed: Boolean(editorModel?.tiptap?.isDestroyed),
      },
    });
  }, []);

  const setEditorModelEditable = React.useCallback(
    (editorModel, editable, phase, switchVersion) => {
      if (!isCurrentEditorSetup(editorModel, switchVersion)) {
        addStaleEditorSetupBreadcrumb(phase, editorModel, switchVersion);
        return null;
      }

      const tiptap = editorModel.tiptap;
      if (typeof tiptap?.setEditable !== "function") {
        Sentry.addBreadcrumb({
          category: "tiptapdocumentsb",
          level: "info",
          message: "Skipped Tiptap setEditable because editor is not ready",
          data: {
            phase,
            documentId: documentIdRef.current,
            setupSwitchVersion: switchVersion,
            hasTiptap: Boolean(tiptap),
          },
        });
        return tiptap || null;
      }

      try {
        tiptap.setEditable(editable);
      } catch (error) {
        Sentry.addBreadcrumb({
          category: "tiptapdocumentsb",
          level: "info",
          message: "Skipped Tiptap setEditable after editor threw",
          data: {
            phase,
            documentId: documentIdRef.current,
            errorMessage: error?.message,
          },
        });
      }
      return tiptap;
    },
    [addStaleEditorSetupBreadcrumb, isCurrentEditorSetup]
  );

  // Destroy stale editors without letting them clear refs that now belong to a newer
  // editor setup. If this is still the current editor, clear all public editor refs.
  const destroyEditorModel = React.useCallback((editorModel) => {
    if (!editorModel) {
      return;
    }

    const wasCurrentEditor = editorModelRef.current === editorModel;
    const tiptap = editorModel.tiptap;

    if (wasCurrentEditor) {
      editorModelRef.current = null;
      if (!tiptap || tiptapRef.current === tiptap) {
        tiptapRef.current = null;
      }
      propsRef.current.commentStore.setTiptapEditor(null);
      useProjectStore.getState().setManuscriptEditorGetter(null);
      if (typeof window !== "undefined" && (!tiptap || window.tiptap === tiptap)) {
        window.tiptap = null;
      }
    }

    if (!editorModel._destroyed) {
      editorModel.destroy();
    }
  }, []);

  const pushAgentEditSnapshot = useStableCallback((stack, snapshot) => {
    if (!snapshot) return;
    stack.push(snapshot);
    if (stack.length > 20) {
      stack.shift();
    }
  });

  const captureAgentEditUndoSnapshot = useStableCallback(() => {
    if (!tiptapRef.current) return;
    // Keep the first pre-edit snapshot for this edit wave (tool start may fire more than once).
    if (pendingAgentEditUndoSnapshotRef.current && !agentEditUndoCapturedRef.current) return;
    pendingAgentEditUndoSnapshotRef.current = tiptapRef.current.getJSON();
    pendingAgentEditUndoDepthRef.current = undoDepth(tiptapRef.current.view.state);
    agentEditUndoCapturedRef.current = false;
    agentEditRedoStackRef.current = [];
    nativeUndoRedoSnapshotsRef.current = [];
  });

  const recordAgentEditUndoSnapshot = useStableCallback(() => {
    if (agentEditUndoCapturedRef.current) return;
    if (!pendingAgentEditUndoSnapshotRef.current) return;
    if (
      tiptapRef.current
      && undoSnapshotMatchesCurrentEditorDoc(tiptapRef.current, pendingAgentEditUndoSnapshotRef.current)
    ) {
      pendingAgentEditUndoSnapshotRef.current = null;
      pendingAgentEditUndoDepthRef.current = null;
      agentEditUndoCapturedRef.current = true;
      return;
    }
    const envelope = {
      doc: pendingAgentEditUndoSnapshotRef.current,
      nativeUndoDepthAtCapture: tiptapRef.current ? undoDepth(tiptapRef.current.view.state) : 0,
      postAgentDoc: tiptapRef.current ? tiptapRef.current.getJSON() : null,
    };
    pushAgentEditSnapshot(agentEditUndoStackRef.current, envelope);
    pendingAgentEditUndoSnapshotRef.current = null;
    pendingAgentEditUndoDepthRef.current = null;
    agentEditUndoCapturedRef.current = true;
  });

  const applyAgentEditUndoSnapshot = useStableCallback(async (snapshot, nativeUndoDocs = [], { waitForEditorSync = false } = {}) => {
    if (!tiptapRef.current || !snapshot) return false;
    if (!editorDocDiffersFromUndoSnapshot(tiptapRef.current, snapshot)) return false;

    const currentDoc = tiptapRef.current.getJSON();
    const currentRedoDepthVal = redoDepth(tiptapRef.current.view.state);

    agentEditRedoStackRef.current = rebaseRedoEntries(agentEditRedoStackRef.current, currentRedoDepthVal);
    const redoEntries = buildRedoEntries(currentDoc, currentRedoDepthVal, nativeUndoDocs);
    for (const entry of redoEntries) {
      pushAgentEditSnapshot(agentEditRedoStackRef.current, entry);
    }
    editorHistoryMutationRef.current = true;
    disableInsertUnmarkingRef.current = true;
    let applied = false;
    try {
      const ydoc = resolveCollaborationYdoc(tiptapRef.current, editorModelRef.current);
      if (ydoc) {
        applied = applyAgentManuscriptUndoSnapshotToCollaborationYdoc(ydoc, snapshot);
      } else {
        const { state, view } = tiptapRef.current;
        const newDoc = state.schema.nodeFromJSON(snapshot);
        const tr = state.tr.replaceWith(0, state.doc.content.size, newDoc.content);
        tr.setMeta("addToHistory", false);
        view.dispatch(tr);
        applied = true;
      }
      if (!applied) {
        for (let i = 0; i < redoEntries.length; i += 1) {
          agentEditRedoStackRef.current.pop();
        }
        return false;
      }

      // Verify the restore took effect. Try full JSON match first; fall back to
      // text-only comparison because stripUnknownMarks during Ydoc restore strips
      // marks like "inserted", making JSON never match while text content is correct.
      let matched = waitForEditorSync
        ? await waitForEditorDocToMatchUndoSnapshot(tiptapRef.current, snapshot)
        : editorDocMatchesUndoSnapshot(tiptapRef.current, snapshot);
      if (!matched && ydoc) {
        const snapshotText = extractTextFromDocJson(snapshot);
        if (waitForEditorSync) {
          const maxAttempts = 24;
          const intervalMs = 50;
          for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            if (tiptapRef.current.state.doc.textContent === snapshotText) { matched = true; break; }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
          }
        } else {
          matched = tiptapRef.current.state.doc.textContent === snapshotText;
        }
      }
      if (!matched) {
        for (let i = 0; i < redoEntries.length; i += 1) {
          agentEditRedoStackRef.current.pop();
        }
        return false;
      }

      tiptapRef.current.chain().focus().scrollIntoView().run();
      return true;
    } finally {
      disableInsertUnmarkingRef.current = false;
      editorHistoryMutationRef.current = false;
    }
  });

  const restoreLatestAgentEditUndoSnapshot = useStableCallback(async (nativeUndoDocs = [], { waitForEditorSync = false } = {}) => {
    if (!tiptapRef.current || agentEditUndoStackRef.current.length === 0) return false;
    const envelope = agentEditUndoStackRef.current[agentEditUndoStackRef.current.length - 1];
    if (!envelope) return false;
    const ok = await applyAgentEditUndoSnapshot(envelope.doc, nativeUndoDocs, { waitForEditorSync });
    if (!ok) return false;
    agentEditUndoStackRef.current.pop();
    return true;
  });

  /** Commit pre-edit snapshot when Yjs sync did not record it before tool_complete. */
  const commitPendingAgentEditUndoSnapshot = useStableCallback(() => {
    recordAgentEditUndoSnapshot();
  });

  const restoreLatestAgentEditRedoSnapshot = useStableCallback(async ({ waitForEditorSync = false } = {}) => {
    if (!tiptapRef.current || agentEditRedoStackRef.current.length === 0) return false;
    const envelope = agentEditRedoStackRef.current[agentEditRedoStackRef.current.length - 1];
    if (!envelope) return false;
    if (!editorDocDiffersFromUndoSnapshot(tiptapRef.current, envelope.doc)) return false;

    const currentUndoDepthNow = undoDepth(tiptapRef.current.view.state);
    pushAgentEditSnapshot(agentEditUndoStackRef.current, {
      doc: tiptapRef.current.getJSON(),
      nativeUndoDepthAtCapture: currentUndoDepthNow,
    });
    editorHistoryMutationRef.current = true;
    disableInsertUnmarkingRef.current = true;
    let applied = false;
    try {
      const ydoc = resolveCollaborationYdoc(tiptapRef.current, editorModelRef.current);
      if (ydoc) {
        applied = applyAgentManuscriptUndoSnapshotToCollaborationYdoc(ydoc, envelope.doc);
      } else {
        const { state, view } = tiptapRef.current;
        const newDoc = state.schema.nodeFromJSON(envelope.doc);
        const tr = state.tr.replaceWith(0, state.doc.content.size, newDoc.content);
        tr.setMeta("addToHistory", false);
        view.dispatch(tr);
        applied = true;
      }
      if (!applied) {
        agentEditUndoStackRef.current.pop();
        return false;
      }

      const matched = waitForEditorSync
        ? await waitForEditorDocToMatchUndoSnapshot(tiptapRef.current, envelope.doc)
        : editorDocMatchesUndoSnapshot(tiptapRef.current, envelope.doc);
      if (!matched) {
        agentEditUndoStackRef.current.pop();
        return false;
      }

      agentEditRedoStackRef.current.pop();
      tiptapRef.current.chain().focus().scrollIntoView().run();
      return true;
    } finally {
      disableInsertUnmarkingRef.current = false;
      editorHistoryMutationRef.current = false;
    }
  });

  const runNativeHistoryCommand = useStableCallback((commandName) => {
    if (!tiptapRef.current) return false;
    const canRun = tiptapRef.current.can?.()[commandName]?.() === true;
    if (!canRun) return false;

    editorHistoryMutationRef.current = true;
    try {
      return tiptapRef.current.chain().focus()[commandName]().run();
    } finally {
      editorHistoryMutationRef.current = false;
    }
  });

  const handleEditorUndo = useStableCallback(async ({ waitForEditorSync = false, preferAgentSnapshot = false } = {}) => {
    if (!tiptapRef.current) return false;
    const currentDepth = undoDepth(tiptapRef.current.view.state);

    // Pending (not yet committed) agent snapshot — checked first because a pending
    // snapshot always represents the most recent agent operation and must not be
    // skipped in favor of an older committed snapshot.
    if (pendingAgentEditUndoSnapshotRef.current && !agentEditUndoCapturedRef.current) {
      const pendingEnvelope = {
        doc: pendingAgentEditUndoSnapshotRef.current,
        nativeUndoDepthAtCapture: 0,
      };
      if (!preferAgentSnapshot && shouldUseNativeUndo(currentDepth, pendingEnvelope)) {
        const docBeforeNativeUndo = tiptapRef.current.getJSON();
        const jsonBeforeUndo = JSON.stringify(docBeforeNativeUndo);
        if (runNativeHistoryCommand("undo")) {
          const jsonAfterUndo = JSON.stringify(tiptapRef.current.getJSON());
          if (jsonBeforeUndo !== jsonAfterUndo) {
            pushAgentEditSnapshot(nativeUndoRedoSnapshotsRef.current, docBeforeNativeUndo);
            return true;
          }
          runNativeHistoryCommand("redo");
        }
      }
      // Consume the pending snapshot and preserve every newer manual state for redo.
      const nativeUndoDocs = nativeUndoRedoSnapshotsRef.current;
      nativeUndoRedoSnapshotsRef.current = [];
      const ok = await applyAgentEditUndoSnapshot(pendingEnvelope.doc, nativeUndoDocs, { waitForEditorSync });
      if (ok) {
        pendingAgentEditUndoSnapshotRef.current = null;
        pendingAgentEditUndoDepthRef.current = null;
        agentEditUndoCapturedRef.current = true;
      }
      return ok;
    }

    // Committed agent snapshot — check if native history grew since capture
    if (agentEditUndoStackRef.current.length > 0) {
      const topSnapshot = agentEditUndoStackRef.current[agentEditUndoStackRef.current.length - 1];
      if (!preferAgentSnapshot && shouldUseNativeUndo(currentDepth, topSnapshot)) {
        const docBeforeNativeUndo = tiptapRef.current.getJSON();
        const jsonBeforeUndo = JSON.stringify(docBeforeNativeUndo);
        if (runNativeHistoryCommand("undo")) {
          const jsonAfterUndo = JSON.stringify(tiptapRef.current.getJSON());
          if (jsonBeforeUndo !== jsonAfterUndo) {
            pushAgentEditSnapshot(nativeUndoRedoSnapshotsRef.current, docBeforeNativeUndo);
            return true;
          }
          // Native undo was a no-op (stale history after Ydoc restore) — redo to restore depth
          runNativeHistoryCommand("redo");
        }
      }

      // Check for intermediate manual edits that native history can't reach
      // (happens after a full-doc restore wiped native history).
      // Compare text content only — marks may differ after Ydoc round-trips
      // (e.g., "inserted" marks get stripped by stripUnknownMarks during Ydoc restoration,
      // so full JSON comparison via editorDocDiffersFromUndoSnapshot would always detect a
      // spurious difference and waitForEditorDocToMatchUndoSnapshot would time out).
      // Skip when preferAgentSnapshot is set (chat undo) — it must jump straight to the
      // pre-agent snapshot, not stop at the intermediate post-agent state.
      if (!preferAgentSnapshot && topSnapshot.postAgentDoc) {
        const currentText = tiptapRef.current.state.doc.textContent;
        const postAgentText = extractTextFromDocJson(topSnapshot.postAgentDoc);
        if (currentText !== postAgentText) {
          // Intermediate manual edits exist — restore postAgentDoc state.
          // Use direct Ydoc restore because applyAgentEditUndoSnapshot's JSON match
          // check fails when marks differ after stripUnknownMarks sanitization.
          const nativeUndoDocs = nativeUndoRedoSnapshotsRef.current;
          nativeUndoRedoSnapshotsRef.current = [];
          const currentDoc = tiptapRef.current.getJSON();
          const currentRedoDepthVal = redoDepth(tiptapRef.current.view.state);
          agentEditRedoStackRef.current = rebaseRedoEntries(agentEditRedoStackRef.current, currentRedoDepthVal);
          const redoEntries = buildRedoEntries(currentDoc, currentRedoDepthVal, nativeUndoDocs);
          for (const entry of redoEntries) {
            pushAgentEditSnapshot(agentEditRedoStackRef.current, entry);
          }
          editorHistoryMutationRef.current = true;
          disableInsertUnmarkingRef.current = true;
          try {
            let applied = false;
            const ydoc = resolveCollaborationYdoc(tiptapRef.current, editorModelRef.current);
            if (ydoc) {
              applied = applyAgentManuscriptUndoSnapshotToCollaborationYdoc(ydoc, topSnapshot.postAgentDoc);
            } else {
              const { state, view } = tiptapRef.current;
              const newDoc = state.schema.nodeFromJSON(topSnapshot.postAgentDoc);
              const tr = state.tr.replaceWith(0, state.doc.content.size, newDoc.content);
              tr.setMeta("addToHistory", false);
              view.dispatch(tr);
              applied = true;
            }
            if (!applied) {
              for (let i = 0; i < redoEntries.length; i += 1) {
                agentEditRedoStackRef.current.pop();
              }
              return false;
            }
            if (waitForEditorSync) {
              let matched = false;
              for (let attempt = 0; attempt < 24; attempt += 1) {
                if (tiptapRef.current.state.doc.textContent === postAgentText) { matched = true; break; }
                await new Promise((resolve) => setTimeout(resolve, 50));
              }
              if (!matched) {
                for (let i = 0; i < redoEntries.length; i += 1) {
                  agentEditRedoStackRef.current.pop();
                }
                return false;
              }
            }
            tiptapRef.current.chain().focus().scrollIntoView().run();
            return true;
          } finally {
            disableInsertUnmarkingRef.current = false;
            editorHistoryMutationRef.current = false;
          }
        }
      }

      // About to consume the agent snapshot — preserve every newer manual state for redo.
      const nativeUndoDocs = nativeUndoRedoSnapshotsRef.current;
      nativeUndoRedoSnapshotsRef.current = [];
      return restoreLatestAgentEditUndoSnapshot(nativeUndoDocs, { waitForEditorSync });
    }

    // No agent snapshots — pure native undo (chat undo should not fall back here).
    if (preferAgentSnapshot) return false;
    return runNativeHistoryCommand("undo");
  });

  const handleEditorRedo = useStableCallback(async ({ waitForEditorSync = false } = {}) => {
    if (!tiptapRef.current) return false;
    const currentRedo = redoDepth(tiptapRef.current.view.state);

    if (agentEditRedoStackRef.current.length > 0) {
      const topRedoSnapshot = agentEditRedoStackRef.current[agentEditRedoStackRef.current.length - 1];
      if (shouldUseNativeRedo(currentRedo, topRedoSnapshot)) {
        if (runNativeHistoryCommand("redo")) return true;
      }
      return restoreLatestAgentEditRedoSnapshot({ waitForEditorSync });
    }

    const didNativeRedo = runNativeHistoryCommand("redo");
    if (didNativeRedo && nativeUndoRedoSnapshotsRef.current.length > 0) {
      nativeUndoRedoSnapshotsRef.current.pop();
    }
    return didNativeRedo;
  });

  const restoreTrashedItemFromBanner = useStableCallback(() => {
    const { trashedItem } = propsRef.current;
    if (!trashedItem?.item?.restore) return;
    if (trashedItem.type === "chat") {
      const chatId = trashedItem.item.id;
      void trashedItem.item.restore(() => {
        propsRef.current.projectStore.exitTrashView?.();
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.CHAT_RESTORED_FROM_TRASH, true, { chatId });
      });
    } else {
      void trashedItem.item.restore();
    }
  });

  const unCollapseRightBar = useStableCallback(() => {
    const { setRightBarCollapsed, columnWidths, project } = propsRef.current;
    const importBlocksRightBar = project?.importStep && project.importStep !== "CANCELLED";
    if (importBlocksRightBar) return;

    const rightBarCollapsed = columnWidths.right.collapsed;
    if (rightBarCollapsed) {
      setRightBarCollapsed(false);
    }
    propsRef.current?.emitSudoLayoutEvent?.("focus_history");
  });

  const setRightBarCollapsedWrapper = useStableCallback((collapse) => {
    const { setRightBarCollapsed, project } = propsRef.current;
    const importBlocksRightBar = project?.importStep && project.importStep !== "CANCELLED";
    if (importBlocksRightBar && !collapse) return;

    if (collapse) {
      setRightBarCollapsed(true);
    } else {
      setRightBarCollapsed(false);
    }
  });

  const beforeUnloadWarning = useStableCallback((event) => {
    if (pendingChanges()) {
      event.preventDefault();
      return (event.returnValue = "You have unsaved changes to this doc. Are you sure you want to exit?");
    }
  });

  // Returns a window of text to consider for completion
  // minWords: the min number of words required for consideration.
  // Returns [window, gap]
  //   where if we don't have the min words, then window is null, and gap is number of words needed
  const wormholeConsideration = useStableCallback((targetTokens = 2000) => {
    const range = getSelection();
    let text = "";
    const index = range.index;

    if (range.length > 0) {
      // a selection of text
      text = range.text;

      // there's a selection, use that
      return [text, null, null];
    } else {
      // using cursor

      // get text from doc up to this point
      text = tiptapRef.current.view.state.doc.textBetween(0, range.index, "\n");
    }

    const { consideration, windowStart } = getBackwardTokenWindow(text, index, targetTokens);

    return [consideration, null, windowStart];
  });

  // Returns a window of text to consider for completion
  // minWords: the min number of words required for consideration.
  // Returns [window, gap]
  //   where if we don't have the min words, then window is null, and gap is number of words needed
  const pluginConsideration = useStableCallback((targetWords = 2000, range = getSelection()) => {
    const index = range.index;

    const text = tiptapRef.current.view.state.doc.textBetween(0, index, "\n");

    return getBackwardWordWindow(text, index, targetWords);
  });

  const pluginConsiderationForward = useStableCallback((targetWords = 2000, range = getSelection()) => {
    const docSize = tiptapRef.current.view.state.doc.content.size;

    const text = tiptapRef.current.view.state.doc.textBetween(range.index + range.length, docSize, "\n");

    return getForwardWordWindow(text, targetWords);
  });

  const expandConsideration = useStableCallback((targetTokens, range) => {
    if (!range) {
      throw new Error("range required");
    }

    // get text up to the start of the selection
    const text = tiptapRef.current.view.state.doc.textBetween(0, range.index, "\n");

    return getExpandTokenWindow(text, targetTokens);
  });

  // Gets the text to consider for a description by searching backwards and forwards from the cursor
  const descriptionConsideration = useStableCallback((range) => {
    if (!range) {
      throw "No range of selection found. User is likely not in the editor.";
    }

    const text = tiptapRef.current.view.state.doc.textBetween(0, range.index, "\n");
    const endIndex = tiptapRef.current.view.state.doc.content.size;
    const afterText = tiptapRef.current.view.state.doc.textBetween(range.index, endIndex, "\n");

    return getDescriptionConsiderationFromText(text, afterText, range.index);
  });

  const {
    refreshCards,
    getCards,
    prependNewCard,
    markCardStreamSettled,
    deleteCard,
    deleteCardItem,
    onNextPage,
    onChangeTab,
    onCardFavorite,
    onCardUnfavorite,
    onDeleteFavorite,
    onFavoriteReorder,
    addDraftPlaceholderCard,
    appendDraftPlaceholderContent,
    linkDraftPlaceholderToCardId,
    finalizeDraftPlaceholder,
    removeDraftPlaceholderCard,
  } = useRightBarCards({
    stateRef,
    setMergedState,
    documentIdRef,
    documentSwitchVersionRef,
    cardQueriesRef,
    favoritesStoreRef,
    placeholderRafIdRef,
    unCollapseRightBar,
    stride,
  });

  // Stops command mode when it is running
  const stopCommandMode = useStableCallback(() => {
    setMergedState({ escapeCommandMode: true, commandModeInserting: false });
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
  });

  // Key presses on the entire document
  const onGlobalKeyPressed = useStableCallback((e) => {
    // Keys originating inside the editor container are handled by the
    // editor-scoped onKeyPressed listener; skipping them here prevents
    // shortcuts like Cmd+K/Cmd+F from double-firing (open-then-close).
    if (editorContainerRef.current?.contains?.(e.target)) {
      return;
    }
    // Need to stop command mode both at editor Escape AND outside escape, since the editor escape doesn't always fire
    if (stateRef.current.commandModeInserting) {
      e.preventDefault();
      if (e.key === "Escape") {
        stopCommandMode();
      }
      return;
    }
    // if (e.key === "." && (e.metaKey || e.ctrlKey)) {
    //   e.preventDefault();
    //   tiptapRef.current.commands.toggleTrackChangeStatus();
    //   return;
    // }

    if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      toggleCommandMenu();

      return;
    }

    // Cmd/Ctrl+F also handled here (in addition to the editor-scoped handler) so
    // the shortcut works when focus is anywhere in the parent document — e.g.
    // after clicking a document result row in the Find & Replace panel itself.
    if (e.key === "f" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
      e.preventDefault();
      const next = !stateRef.current.findAndReplaceOpen;
      applyFindAndReplaceOpenState({
        findAndReplaceOpen: next,
        setState: (nextState) => setMergedState(nextState),
        setForceRenderSearchables: useStoryBibleStore.getState().setForceRenderSearchables,
      });
      return;
    }

    if (e.key === "h" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("sudo-open-document-history", { bubbles: false }));
      return;
    }
  });

  const getDocumentJSON = useStableCallback(() => {
    return tiptapRef.current?.getJSON();
  });

  const toggleCommandMenu = useStableCallback(() => {
    const editorModel = editorModelRef.current;
    if (!editorModel) {
      return;
    }

    editorModel.saveSelection();
    editorModel.setLastCursorPosition();

    if (!propsRef.current.subscription.hasAccessToQuickEdit) {
      return;
    }
    editorModel.clearAllHighlights();
    // Quick Edit paints its own highlight; the persisted-selection fake
    // highlight must never coexist with it.
    tiptapRef.current?.commands.clearPersistedSelection();
    if (!stateRef.current.showCommandMenu) {
      // don't open if user does not have access to features
      if (!propsRef.current.subscription.hasAccessToFeatures) {
        showError("Your free trial or membership has expired or is overdue. Subscribe to access AI features.");
        return;
      }

      // don't open if selection is too big
      if (stateRef.current.highlightedWordCount > MAX_QUICK_EDIT_HIGHLIGHTED_WORDS) {
        showError(`Selection too large, Quick Edit is limited to ${MAX_QUICK_EDIT_HIGHLIGHTED_WORDS} words or less.`);
        return;
      }
      const range = getSelection();
      editorModel.highlight(range.index, range.index + range.length, true); // save the initial selection range

      // close the document linking menu if it is open
      propsRef.current.projectStore.setShowLinkingMenu(false);
      // don't show new document CTAs while command menu is open
      propsRef.current.projectStore.setShowNewDocumentCTAs(false);
    }

    setMergedState({ showCommandMenu: !stateRef.current.showCommandMenu });
  });

  const dismissCommandMenu = useStableCallback(() => {
    const editorModel = editorModelRef.current;
    editorModel?.clearAllHighlights();
    setMergedState({ showCommandMenu: false });
    editorModel?.restoreSelection();
  });

  const setFindAndReplaceOpen = useStableCallback((findAndReplaceOpen) => {
    applyFindAndReplaceOpenState({
      findAndReplaceOpen,
      setState: (state) => setMergedState(state),
      setForceRenderSearchables: useStoryBibleStore.getState().setForceRenderSearchables,
    });
  });

  const toggleFindAndReplaceOpen = useStableCallback(() => {
    setFindAndReplaceOpen(!stateRef.current.findAndReplaceOpen);
  });

  const editorComputedStyles = React.useMemo(
    () =>
      getEditorComputedStyles({
        editorOptions: props.editorOptions,
        fontFamily: UserData.toFontFamily(props.editorOptions.font),
        theme: props.theme,
      }),
    [
      props.editorOptions?.font,
      props.editorOptions?.fontSize,
      props.editorOptions?.lineHeight,
      props.editorOptions?.paragraphSpacing,
      props.editorOptions?.textIndent,
      props.theme,
    ],
  );

  const hideLinkingMenu = useStableCallback(() => {
    propsRef.current.projectStore.setShowLinkingMenu(false);
  });

  const toggleSpellCheck = useStableCallback(() => {
    const currentProps = propsRef.current;
    const { editorOptions, setEditorOptions } = currentProps;
    // Treat one-on-one-off (legacy mixed state) as "currently on"
    // so the next click turns both off — gives migrated users a clean reset.
    const currentlyOn = editorOptions.spellCheckEnabled || editorOptions.grammarCheckEnabled;
    const nextValue = !currentlyOn;
    setEditorOptions({
      ...editorOptions,
      spellCheckEnabled: nextValue,
      grammarCheckEnabled: nextValue,
    });
    if (tiptapRef.current) {
      if (documentIdRef.current && tiptapRef.current.commands.setGrammarDocumentId) {
        tiptapRef.current.commands.setGrammarDocumentId(documentIdRef.current);
      }
      tiptapRef.current.commands.setSpellcheckEnabled(nextValue);
    }
    if (currentProps.sudowriteStore?.userData) {
      currentProps.sudowriteStore.userData.update({
        spellCheckEnabled: nextValue,
        grammarCheckEnabled: nextValue,
      });
    }
    void sendAppEvent("Spellcheck Toggled", { enabled: nextValue });
  });

  const handleOpenBrainstorm = useStableCallback((e) => {
    e?.preventDefault?.();
    setMergedState({ brainstormOpen: true });
    setFindAndReplaceOpen(false);
  });

  const openBrainstorm = useStableCallback((id) => {
    setMergedState({ brainstormOpen: id });
  });

  const setMemory = useStableCallback((val) => {
    setMergedState({ memory: val });
  });

  // Intercept editor history shortcuts before TipTap's own keymap sees them. Otherwise the
  // native keymap and this hybrid agent/native handler can both consume one physical keypress.
  const onEditorHistoryKeyDownCapture = useStableCallback((e) => {
    if (stateRef.current.commandModeInserting) return;
    if (!tiptapRef.current?.view?.dom?.contains?.(e.target)) return;
    const isUndo = (e.metaKey || e.ctrlKey) && !e.shiftKey && e.key?.toLowerCase() === "z";
    const isRedo = (e.metaKey || e.ctrlKey) && ((e.shiftKey && e.key?.toLowerCase() === "z") || e.key?.toLowerCase() === "y");
    if (!isUndo && !isRedo) return;

    if (isUndo) {
      const mightAgentUndo =
        agentEditUndoStackRef.current.length > 0
        || (pendingAgentEditUndoSnapshotRef.current && !agentEditUndoCapturedRef.current);
      if (mightAgentUndo || tiptapRef.current?.can?.().undo?.() === true) {
        e.preventDefault();
        e.stopPropagation();
        void handleEditorUndo({ waitForEditorSync: true });
      }
      return;
    }

    const mightAgentRedo = agentEditRedoStackRef.current.length > 0;
    if (mightAgentRedo || tiptapRef.current?.can?.().redo?.() === true) {
      e.preventDefault();
      e.stopPropagation();
      void handleEditorRedo({ waitForEditorSync: true });
    }
  });

  // Key presses within the editor container
  const onKeyPressed = useStableCallback((e) => {
    if (stateRef.current.commandModeInserting) {
      e.preventDefault();
      if (e.key === "Escape") {
        stopCommandMode();
      }
      return;
    }

    // more global keypress handling when Command Menu is open
    if (stateRef.current.showCommandMenu) {
      if (e.key === "Escape") {
        e.preventDefault();
        dismissCommandMenu();
        return;
      }
    }

    if (e.key === "Escape") {
      setMergedState({ focus: false });
    } else if (e.key === "." && (e.metaKey || e.ctrlKey) && propsRef.current.subscription.hasAccessToQuickEdit) {
      tiptapRef.current.commands.toggleTrackChangeStatus();
      return;
    } else if (e.key === "'" && (e.metaKey || e.ctrlKey) && propsRef.current.subscription.hasAccessToQuickEdit) {
      try {
        tiptapRef.current.commands.acceptAllChanges();
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            documentId: documentIdRef.current,
            user: getUserId(),
            func: "acceptAllChanges",
          },
        });
      }
      return;
    } else if (e.key === "k" && (e.metaKey || e.ctrlKey) && propsRef.current.subscription.hasAccessToQuickEdit) {
      e.preventDefault();
      toggleCommandMenu();
    } else if (e.key === "h" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("sudo-open-document-history", { bubbles: false }));
    } else if (e.key === "f" && e.metaKey && e.shiftKey) {
      setMergedState({ focus: !stateRef.current.focus });
    } else if (e.key === "f" && (e.metaKey || e.ctrlKey)) {
      if (!stateRef.current.findAndReplaceOpen) {
        e.preventDefault();
      }
      toggleFindAndReplaceOpen();
    } else if (e.keyCode == 13 && (e.metaKey || e.ctrlKey)) {
      // only do Write if we're not in the command menu
      if (!stateRef.current.showCommandMenu) {
        e.preventDefault();
        const currentEditor = tiptapRef.current;
        if (!currentEditor?.getText?.()) return;
        const nonce = makeId(15);
        pluginOpenWormhole(undefined, `new_paragraph_${nonce}`);
      }
    } else if (e.key == "s" && (e.metaKey || e.ctrlKey)) {
      // don't trigger default save page behavior
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      const range = getSelection();
      if (range.index <= 1 && titleEditorRef.current) {
        titleEditorRef.current.focus();
      }
    } else if ((e.metaKey || e.ctrlKey) && e.key === "/") {
      propsRef.current.toggleFocusMode();
    }
  });

  const updateWordCount = useStableCallback((textOverride) => {
    const text = typeof textOverride === "string" ? textOverride : tiptapRef.current?.getText?.();
    if (typeof text !== "string") return;
    const count = getWordCountFromText(text, countWords);

    if (stateRef.current.wordCount !== count) {
      setMergedState({
        wordCount: count,
      });
    }

    // we no longer display the initial state for first draft
    // if (count === 0) {
    //   setMergedState({ commandModeState: "initial" });
    // }
  });

  const setSeenTips = useStableCallback((arr) => {
    setMergedState({ seenTips: arr });
  });

  const updateHighlightedWordCount = useStableCallback((range) => {
    const nextState = getHighlightedWordCountState(range, stateRef.current.highlightedWordCount, countWords);

    if (nextState) {
      setMergedState(nextState);
    }
  });

  const onSelectionChange = React.useMemo(
    () =>
      debounce((event) => {
        if (!shouldHandleTiptapSelectionUpdate(tiptapRef.current, event?.editor, componentUnmountedRef.current)) {
          return;
        }

        if (propsRef.current.storyBibleStore.isProseStreamingRef.current) return;

        // save current range for comparisons to prevent race conditions
        const range = getSelection();

        setMergedState({ range: range });

        if (range) {
          setMergedState({
            guidanceOpen: false,
          });

          updateHighlightedWordCount(range);

          if (!loadingDocumentRef.current) {
            updateSelectionIndex(range);
          }

          if (range.length > 0) {
            const selectionText = range.text;

            const words = countWords(selectionText);
            const selectionActionState = getSelectionActionState(words);

            propsRef.current.stickyTourStore.setSelectedWordCount(words);
            if (selectionActionState.shouldSendTextSelectedSignal) {
              propsRef.current.sendFlowSignal(TourSignal.TEXT_SELECTED);
            }

            setMergedState({
              rewriteSelection: selectionActionState.rewriteSelection,
              expandSelection: selectionActionState.expandSelection,
              visualizeSelection: selectionActionState.visualizeSelection,
            });

            if (selectionActionState.shouldFetchRelatedWords) {
              relatedWordsStoreRef.current.get(selectionText).then((related) => {
                // Mirror the class component's legacy guard for canary parity.
                // Re-reading the current selection here changes when related-word UI appears.
                if (range.text.length === range?.length && range.index === range?.index) {
                  setMergedState({
                    relatedClusters: related || null,
                    describeSelection: selectionActionState.describeSelection,
                    commandMenuSelection: selectionActionState.commandMenuSelection,
                  });
                }
              });
            } else {
              setMergedState({
                describeSelection: selectionActionState.describeSelection,
                commandMenuSelection: selectionActionState.commandMenuSelection,
                relatedClusters: null,
              });
            }

            setMergedState({
              selection: selectionText,
              selectionRange: range,
            });
          } else {
            propsRef.current.stickyTourStore.setSelectedWordCount(0);
            setMergedState(getCollapsedSelectionState(range));
          }
        } else {
          // TODO: will likely need to get rid of menu based on focus

          if (stateRef.current.selection === null && stateRef.current.selectionRange === null && stateRef.current.highlightedWordCount === null) {
            return;
          }

          // set state on the next tick so that the highlight menu component has time to register the click before it dissappears
          setTimeout(() => {
            setMergedState(getClearedSelectionState());
          }, 0);
        }

        // if we're changing selection, close menus
        if (stateRef.current.labsOpen || stateRef.current.storyToolOpen || stateRef.current.toneOpen) {
          setMergedState({
            labsOpen: false,
            storyToolOpen: false,
            toneOpen: false,
          });
        }

        // Store the last known cursor position for resize events
        if (tiptapRef.current && tiptapRef.current.view && tiptapRef.current.state.selection) {
          lastKnownCursorPositionRef.current = tiptapRef.current.state.selection.from;
        }
      }, 250),
    [],
  );

  // Updates the selection index based on cursor position
  const updateSelectionIndex = React.useMemo(
    () =>
      debounce(
        (range) => {
          documentModelRef.current.saveSelectionIndex(range.index);
        },
        1000,
        {
          maxWait: 2000,
        },
      ),
    [],
  );

  const generateDocumentTitle = React.useMemo(
    () =>
      debounce((text, wordCount) => {
        if (
          propsRef.current.project.documentIsUntitled(documentIdRef.current) &&
          wordCount >= MIN_DOC_TITLING_WORDS &&
          !propsRef.current.isEditingDocumentTitle &&
          propsRef.current.project.importSource !== "scrivener"
        ) {
          propsRef.current.project.automaticallyTitleDocument(text, documentIdRef.current);
        }
      }, 2000),
    [],
  );

  // All the React state changes that need to occur when the user alters
  // their document. This is debounced.
  const updatesOnTextChange = React.useMemo(
    () =>
      debounce(
        (text) => {
          updateWordCount(text);
        },
        500,
        {
          maxWait: 2000,
        },
      ),
    [],
  );

  const onTextChange = useStableCallback(({ editor, transaction }) => {
    const currentEditor = tiptapRef.current;
    if (componentUnmountedRef.current || !currentEditor || editor !== currentEditor) return;

    // Any document change outside our own undo/redo operations starts a new history branch.
    // Match native editor semantics by invalidating both custom redo states and any manual
    // snapshots that were waiting to be converted after crossing an agent edit.
    if (!editorHistoryMutationRef.current) {
      agentEditRedoStackRef.current = [];
      nativeUndoRedoSnapshotsRef.current = [];
    }

    const isYjsSyncUpdate = !!transaction?.getMeta?.(ySyncPluginKey);

    // Update word count even during prose streaming so the status bar stays current
    if (propsRef.current.storyBibleStore.isProseStreamingRef.current) {
      updatesOnTextChange(editor.getText());
      return;
    }

    let text = editor.getText();
    const projectUiState = getTextChangeProjectUiState(text);

    setMergedState(getTextChangeResetState(text));

    // is this still necessary as a guard against first draft overlay?
    if (projectUiState.hideLinkingMenu) {
      propsRef.current.projectStore.setShowLinkingMenu(false);
    }
    propsRef.current.projectStore.setShowNewDocumentCTAs(projectUiState.showNewDocumentCTAs);

    updatesOnTextChange(text);
    generateDocumentTitle(text, countWords(text));

    // Unset inserted marks only for local user edits (not Yjs sync/rehydration updates).
    if (
      tiptapRef.current === currentEditor &&
      currentEditor.isActive("inserted") &&
      !disableInsertUnmarkingRef.current &&
      !isYjsSyncUpdate
    ) {
      // Get the current cursor position
      let cursorPos = currentEditor.state.selection.to;

      // expand selection to the entire inserted area, then unset it, and then shrink it back to the cursor
      currentEditor.chain().extendMarkRange("inserted").unsetMark("inserted").run();

      // Create a new selection that doesn't cover any content
      let newSelection = TextSelection.create(currentEditor.state.doc, cursorPos, cursorPos);

      // Apply the new selection to the state
      let tr = currentEditor.state.tr.setSelection(newSelection);

      // Dispatch the transaction
      currentEditor.view.dispatch(tr);
    }
  });

  /**
   * Remote Yjs edits (e.g. agent `edit_document`) dispatch a transaction with scrollIntoView when the
   * local cursor is in view — which jumps the manuscript scroll. Preserve center-column scroll here.
   */
  const bindAgentManuscriptEditHandlers = useStableCallback(() => {
    if (tiptapRef.current && onAgentYSyncTransactionRef.current) {
      tiptapRef.current.off("transaction", onAgentYSyncTransactionRef.current);
    }

    const restoreAgentEditScroll = () => {
      const savedCenter = agentEditSavedScrollTopRef.current;
      const savedInner = agentEditSavedInnerScrollTopRef.current;
      const savedIframe = agentEditSavedIframeScrollTopRef.current;

      if (centerColumnRef?.current && savedCenter != null) {
        centerColumnRef.current.scrollTop = savedCenter;
      }
      if (innerRef?.current && savedInner != null) {
        innerRef.current.scrollTop = savedInner;
      }

      // In focus mode the editor container itself is the scroll box.
      if (editorContainerRef.current && savedIframe != null) {
        editorContainerRef.current.scrollTop = savedIframe;
      }
    };

    const scheduleAgentEditScrollRestore = () => {
      requestAnimationFrame(() => restoreAgentEditScroll());
      // Late scrollIntoView calls can arrive after initial transaction/highlight.
      [24, 72, 160, 320, 700].forEach((delay) => {
        setTimeout(() => restoreAgentEditScroll(), delay);
      });
    };

    onAgentYSyncTransactionRef.current = ({ transaction }) => {
      if (!transaction.docChanged) return;
      if (!transaction.getMeta(ySyncPluginKey)) return;
      recordAgentEditUndoSnapshot();
      // Remote Yjs can dispatch multiple sync transactions; only the first had scroll restore before.
      if (Date.now() > (agentEditScrollPreserveUntilRef.current ?? 0)) return;
      scheduleAgentEditScrollRestore();
    };

    tiptapRef.current.on("transaction", onAgentYSyncTransactionRef.current);

    if (!agentManuscriptWindowListenersBoundRef.current) {
      onAgentEditDocumentStartRef.current = () => {
        agentEditSavedScrollTopRef.current = centerColumnRef?.current?.scrollTop ?? 0;
        agentEditSavedInnerScrollTopRef.current = innerRef?.current?.scrollTop ?? 0;
        agentEditSavedIframeScrollTopRef.current = editorContainerRef.current?.scrollTop ?? 0;
        // y-prosemirror may apply several remote updates; keep restoring for a short window.
        agentEditScrollPreserveUntilRef.current = Date.now() + 4000;
        captureAgentEditUndoSnapshot();
      };

      onAgentHighlightEditRef.current = (e) => {
        const { documentId, highlightSnippet, highlightFullDocument, clear } = e.detail || {};
        if (clear === true) {
          editorModelRef.current?.clearHighlight?.();
          return;
        }
        if (!documentId || !propsRef.current.document || String(propsRef.current.document.id) !== String(documentId)) return;
        if (!highlightFullDocument && !highlightSnippet) return;
        if (!editorModelRef.current?.highlight || !tiptapRef.current?.view) return;
        const provider = editorModelRef.current?.provider;
        const editor = tiptapRef.current;
        const isActive = () => tiptapRef.current === editor && !editor.isDestroyed
          && editorModelRef.current?.provider === provider
          && String(propsRef.current.document?.id) === String(documentId);
        let applied = false;
        let cancelSyncRecovery = () => {};
        let syncHighlightListener = null;
        const detachSyncHighlightListener = () => {
          cancelSyncRecovery();
          if (syncHighlightListener) {
            editor.off("transaction", syncHighlightListener);
            syncHighlightListener = null;
          }
        };
        const tryApply = () => {
          if (!isActive() || applied || !tiptapRef.current?.view || !editorModelRef.current?.highlight) return;
          let range = null;
          if (highlightFullDocument) {
            const docSize = tiptapRef.current.state.doc?.content?.size ?? 0;
            if (docSize > 1) {
              range = { from: 1, to: docSize };
            }
          } else {
            range = findAgentEditHighlightRange(tiptapRef.current.state.doc, highlightSnippet);
          }
          if (!range) return;
          if (!highlightFullDocument && !docRangeHasInsertedMark(tiptapRef.current.state.doc, range.from, range.to)) {
            const insertedSpans = collectInsertedMarkRanges(tiptapRef.current.state.doc);
            const overlapping = insertedSpans.filter((span) => span.from < range.to && span.to > range.from);
            if (overlapping.length > 0) {
              range = expandRangeToInsertedMarkSpan(
                tiptapRef.current.state.doc,
                range.from,
                range.to,
              );
            }
          }
          applied = true;
          detachSyncHighlightListener();
          editorModelRef.current?.clearHighlight?.();
          tiptapRef.current?.commands.clearPersistedSelection?.();
          editorModelRef.current.highlight(range.from, range.to, true, { clearSelection: false });
          try {
            tiptapRef.current.chain().focus().setTextSelection({ from: range.from, to: range.to }).run();
          } catch (_) {
            /* selection sync is best-effort */
          }
          void import("src/components/PersistentChat/applyAgentManuscriptHighlight").then(
            ({ syncManuscriptHighlightToPersistentChat }) => {
              if (!tiptapRef.current || tiptapRef.current.isDestroyed) return;
              syncManuscriptHighlightToPersistentChat({
                tiptap: tiptapRef.current,
                documentId: String(documentId),
                from: range.from,
                to: range.to,
              });
            },
          );
          scheduleAgentEditScrollRestore();
        };
        syncHighlightListener = ({ transaction }) => {
          if (!transaction.docChanged || !transaction.getMeta(ySyncPluginKey)) return;
          tryApply();
        };
        tiptapRef.current.on("transaction", syncHighlightListener);
        requestAnimationFrame(() => {
          tryApply();
          if (applied) return;
          [120, 350, 700, 1400].forEach((delay) => {
            setTimeout(() => tryApply(), delay);
          });
        });
        cancelSyncRecovery = syncAppliedAgentEdit({
          provider,
          isComplete: () => applied,
          isActive,
          onTimeout: (reason) => {
            detachSyncHighlightListener();
            showError(reason === "highlight_not_found"
              ? "Your edit was saved, but we couldn't highlight the changed passage. Check the document for your edit."
              : "Your edit was saved, but we couldn't confirm this editor is up to date. Reload the page to check the saved change.");
          },
        });
        // Also detach if the user navigates away before recovery finishes.
        setTimeout(detachSyncHighlightListener, 10050);
      };

      onAgentApplyManuscriptReplacementRef.current = (e) => {
        const detail = e.detail || {};
        const { documentId, oldText, newText } = detail;
        if (!documentId || !propsRef.current.document || String(propsRef.current.document.id) !== String(documentId)) {
          return;
        }
        if (!tiptapRef.current || tiptapRef.current.isDestroyed) return;
        agentEditSavedScrollTopRef.current = centerColumnRef?.current?.scrollTop ?? 0;
        agentEditSavedInnerScrollTopRef.current = innerRef?.current?.scrollTop ?? 0;
        agentEditSavedIframeScrollTopRef.current = editorContainerRef.current?.scrollTop ?? 0;
        agentEditScrollPreserveUntilRef.current = Date.now() + 4000;
        if (shouldCaptureAgentEditUndoSnapshot({
          hasPendingSnapshot: Boolean(pendingAgentEditUndoSnapshotRef.current),
          pendingSnapshotCommitted: agentEditUndoCapturedRef.current,
        })) {
          captureAgentEditUndoSnapshot();
        }

        try {
          if (editorModelRef.current?.provider?.status === "connected") {
            editorModelRef.current.provider.forceSync();
          } else {
            editorModelRef.current?.provider?.connect?.();
          }
        } catch (_) {
          /* forceSync is best-effort */
        }

        const replacementDetail = {
          documentId,
          oldText,
          newText,
          plainTextStart: detail.plainTextStart,
          plainTextEnd: detail.plainTextEnd,
          pmFrom: detail.pmFrom,
          pmTo: detail.pmTo,
        };
        const highlightSnippet = stripMarkdownForHighlightSnippet(String(newText ?? ""));
        const syncDetectionSnippet = highlightSnippet.slice(0, 160);

        let applied = false;
        let syncApplyListener = null;
        const detachSyncApplyListener = () => {
          if (syncApplyListener && tiptapRef.current) {
            tiptapRef.current.off("transaction", syncApplyListener);
            syncApplyListener = null;
          }
        };

        const highlightSyncedReplacement = () => {
          if (!highlightSnippet || !tiptapRef.current?.view || !editorModelRef.current?.highlight) return;
          let range = findAgentEditHighlightRange(tiptapRef.current.state.doc, highlightSnippet);
          if (!range) return;
          if (!docRangeHasInsertedMark(tiptapRef.current.state.doc, range.from, range.to)) {
            const insertedSpans = collectInsertedMarkRanges(tiptapRef.current.state.doc);
            const overlapping = insertedSpans.filter((span) => span.from < range.to && span.to > range.from);
            if (overlapping.length > 0) {
              range = expandRangeToInsertedMarkSpan(
                tiptapRef.current.state.doc,
                range.from,
                range.to,
              );
            } else {
              return;
            }
          }
          editorModelRef.current?.clearHighlight?.();
          tiptapRef.current?.commands.clearPersistedSelection?.();
          editorModelRef.current.highlight(range.from, range.to, true, { clearSelection: false });
          try {
            tiptapRef.current.chain().focus().setTextSelection({ from: range.from, to: range.to }).run();
          } catch (_) {
            /* selection sync is best-effort */
          }
          void import("src/components/PersistentChat/applyAgentManuscriptHighlight").then(
            ({ syncManuscriptHighlightToPersistentChat }) => {
              if (!tiptapRef.current || tiptapRef.current.isDestroyed) return;
              syncManuscriptHighlightToPersistentChat({
                tiptap: tiptapRef.current,
                documentId: String(documentId),
                from: range.from,
                to: range.to,
              });
            },
          );
          scheduleAgentEditScrollRestore();
        };

        const markAppliedFromRemoteOrLocal = () => {
          if (applied) return;
          applied = true;
          detachSyncApplyListener();
          recordAgentEditUndoSnapshot();
          highlightSyncedReplacement();
          scheduleAgentEditScrollRestore();
        };

        const tryApply = () => {
          if (applied || !tiptapRef.current?.view) return false;
          const ok = applyAgentManuscriptReplacementInEditor(tiptapRef.current, replacementDetail);
          if (ok) {
            markAppliedFromRemoteOrLocal();
            return true;
          }
          if (syncDetectionSnippet) {
            const synced = isAgentManuscriptReplacementSyncedInEditor(
              tiptapRef.current,
              replacementDetail,
              syncDetectionSnippet,
            );
            if (synced) {
              markAppliedFromRemoteOrLocal();
              return true;
            }
          }
          return false;
        };

        syncApplyListener = ({ transaction }) => {
          if (!transaction.docChanged || !transaction.getMeta(ySyncPluginKey)) return;
          tryApply();
        };
        tiptapRef.current.on("transaction", syncApplyListener);

        requestAnimationFrame(() => {
          tryApply();
          if (applied) return;
          [120, 350, 700, 1400, 2800].forEach((delay) => {
            setTimeout(() => tryApply(), delay);
          });
        });
        setTimeout(() => {
          if (!applied) {
            pendingAgentEditUndoSnapshotRef.current = null;
            pendingAgentEditUndoDepthRef.current = null;
            agentEditUndoCapturedRef.current = false;
          }
          detachSyncApplyListener();
        }, 10000);
      };

      onAgentEditDocumentRecordUndoRef.current = () => {
        commitPendingAgentEditUndoSnapshot();
      };

      onAgentManuscriptUndoRef.current = () => {
        const docBefore = tiptapRef.current ? serializeEditorDocJson(tiptapRef.current) : null;
        void handleEditorUndo({ waitForEditorSync: true, preferAgentSnapshot: true }).then((ok) => {
          const docAfter = tiptapRef.current ? serializeEditorDocJson(tiptapRef.current) : null;
          const changed = docBefore !== null && docAfter !== null && docBefore !== docAfter;
          window.dispatchEvent(
            new CustomEvent("sudo-agent-manuscript-undo-result", { bubbles: false, detail: { ok: Boolean(ok && changed) } }),
          );
        });
      };

      window.addEventListener("sudo-agent-edit-document-start", onAgentEditDocumentStartRef.current);
      window.addEventListener("sudo-agent-highlight-edit", onAgentHighlightEditRef.current);
      window.addEventListener(
        "sudo-agent-apply-manuscript-replacement",
        onAgentApplyManuscriptReplacementRef.current,
      );
      window.addEventListener("sudo-agent-edit-document-record-undo", onAgentEditDocumentRecordUndoRef.current);
      window.addEventListener("sudo-agent-manuscript-undo", onAgentManuscriptUndoRef.current);
      agentManuscriptWindowListenersBoundRef.current = true;
    }

    if (onAgentHighlightDismissRef.current) {
      window.document.removeEventListener("mousedown", onAgentHighlightDismissRef.current, true);
    }
    onAgentHighlightDismissRef.current = (event) => {
      // Only clicks inside the editor dismiss the highlight.
      if (!editorContainerRef.current?.contains?.(event.target)) return;
      // Sticky highlights (e.g. Quick Edit success) persist until the user
      // accepts/rejects the change, so don't dismiss them on document clicks.
      if (editorModelRef.current?.isHighlightSticky?.()) return;
      editorModelRef.current?.clearHighlight?.();
      // Agent post-edit highlights also paint the persisted fake selection; clear
      // both when the user clicks back into the manuscript to dismiss them.
      tiptapRef.current?.commands.clearPersistedSelection?.();
    };
    window.document.addEventListener("mousedown", onAgentHighlightDismissRef.current, true);
  });

  const handleCommandMode = useStableCallback(async (text, cached) => {
    if (!text) {
      return false;
    }

    setMergedState({ escapeCommandMode: false });

    const chunks = cached ? cached.split(" ").map((w) => w + " ") : [];
    let streamDone = cached ? true : false;
    let index = 0;

    if (cached) {
      setMergedState({ commandModeInserting: true });
    }

    tiptapRef.current.chain().focus().scrollIntoView().setInserted().run();

    // disable the unsetting of inserted marks
    disableInsertUnmarkingRef.current = true;

    commandModeIntervalRef.current = setInterval(() => {
      if (chunks[index] !== undefined && stateRef.current.commandModeInserting && !stateRef.current.escapeCommandMode) {
        if (index <= 1 && chunks[index] === "\n") {
          index++;
          return;
        }

        if (editorRef.current) editorRef.current.style.caretColor = "transparent";
        const chunkText = convertQuotes(chunks[index], tiptapRef.current);

        tiptapRef.current.commands.scrollIntoView();

        if (chunkText.indexOf("\n") === -1) {
          // no newline present, so we just insert
          tiptapRef.current.commands.insertContent(chunkText);
        } else {
          const lines = splitSingleNewlines(chunkText);

          for (let i = 0; i < lines.length; i++) {
            if (lines[i].length > 0) {
              tiptapRef.current.commands.insertContent(lines[i]);
            }

            if (i < lines.length - 1) {
              tiptapRef.current.commands.insertContent({
                type: "paragraph",
                content: [],
              });

              tiptapRef.current.commands.setInserted();
            }
          }
        }

        // automatic scrolling when streaming in text
        editorRef?.current?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });

        // make sure the text in the editor doesn't overflow the container
        if (propsRef.current.editorSectionRef && editorRef) {
          propsRef.current.editorSectionRef.current.style.height = `${editorRef?.current?.offsetHeight + 150}px`;
        }

        const selection = window.getSelection();
        let rect = null;

        if (selection && selection.rangeCount > 0) {
          try {
            const range = selection.getRangeAt(0);
            rect = range?.getClientRects()[0] || null;
          } catch (error) {
            rect = null;
          }
        }

        if (rect) {
          // MagicCursor positions in editor-container coordinates.
          const containerRect = editorContainerRef.current?.getBoundingClientRect();
          const caretPosition = containerRect ? { x: rect.x - containerRect.left, y: rect.y - containerRect.top } : rect;
          setMergedState({ caretPosition, magicCursorVisible: true });
        }
        index++;
      } else if ((index >= chunks.length && streamDone === true) || stateRef.current.escapeCommandMode) {
        setMergedState({ magicCursorVisible: false });
        clearInterval(commandModeIntervalRef.current);
        commandModeIntervalRef.current = null;
        const currentTiptap = tiptapRef.current;
        currentTiptap?.commands?.unsetInserted?.();
        disableInsertUnmarkingRef.current = true;
        currentTiptap?.setEditable?.(true);

        setTimeout(() => {
          setMergedState({ commandModeInserting: false, caretPosition: { x: -9999, y: -9999 } });
          if (editorRef.current) editorRef.current.style.caretColor = "";
          refreshCards();
        }, 1000);
      }
    }, 50);

    if (cached) return;

    Auth.streamWithIdToken(
      "/api/ai/command",
      {
        summary: text,
        documentId: documentIdRef.current,
      },
      controllerRef.current.signal,
      (chunk) => {
        if (chunk.completionId) {
          streamDone = true;
        }
        const content = chunk.content;

        if (content) {
          if (!stateRef.current.commandModeInserting && !stateRef.current.escapeCommandMode) {
            setMergedState({ commandModeInserting: true });
          }
          const chunkText = content.text;
          chunks.push(chunkText);
        }
      },
      null,
      (e) => {
        if (e.error) {
          if (e.cause instanceof ContentViolationError) {
            showError(CONTENT_POLICY_MESSAGE, 15000);
          } else {
            showError(e.message);
          }

          setMergedState({ commandModeInserting: false, caretPosition: { x: -9999, y: -9999 }, commandModeState: "show" });
        }
      },
    ).catch((e) => {
      showError(e.text);
    });
  });

  // Main entry point for wormhole
  const openWormhole = useStableCallback(({ guidance = null, sessionId = null, constructOnly = false, considerationLength = 2000, forcedOutputCards = false } = {}) => {
    if (!guidance && propsRef.current.writeMode === WRITE_MODE_GUIDED) {
      if (propsRef.current.automaticGuidances && !(sprintRef.current.writeAvailable && selectedWriteModel === "excellent")) {
        return fetchGuidance();
      } else {
        return setMergedState({
          guidanceOpen: true,
        });
      }
    }

    const writeLoadingMessage = "Cannot initiate Write because the editor is still loading.";
    const blockReason = getFeatureActionBlockReason({
      subscription: propsRef.current.subscription,
      creditWaived: sprintRef.current.writeAvailable && selectedWriteModel === "excellent",
      loadingDocument: loadingDocumentRef.current,
      loadingMessage: writeLoadingMessage,
    });
    if (blockReason) {
      showError(blockReason);
      if (blockReason === writeLoadingMessage) {
        Sentry.captureMessage(blockReason);
      }
      return;
    }

    // Ensure editor is focused so ProseMirror returns correct cursor position.
    // Toolbar mousedown can blur the editor, causing getSelection() to return end-of-doc.
    tiptapRef.current.commands.focus();

    let [consideration, gap, start] = wormholeConsideration(considerationLength);

    const params = {};

    params.documentId = documentIdRef.current;

    let ready = true;
    let loading = true;

    // Augment prompt based on the tone unless we're guiding
    params.prompt = consideration;

    if (stateRef.current.engine) {
      params.engine = stateRef.current.engine;
    }

    if (stateRef.current.stop) {
      params.stop = stateRef.current.stop;
    }

    if (stateRef.current.n) {
      params.n = parseInt(stateRef.current.n);
    }

    if (propsRef.current.tone && propsRef.current.writeMode === WRITE_MODE_TONE) {
      params.tone = propsRef.current.tone.value;
    }

    params.outputLength = propsRef.current.editorOptions.wormholeOutputLength;
    params.outputCards = propsRef.current.editorOptions.wormholeOutputCards;
    params.creativitySliderPosition = propsRef.current.editorOptions.creativitySliderPosition;
    params.writeMode = propsRef.current.writeMode.value || DEFAULT_WRITE_MODE.value;
    params.writeModel = resolveBenchmarkWriteModel(selectedWriteModel);
    if (selectedWriteModel === BENCHMARK_WRITE_MODEL) {
      params.benchmarkMode = true;
    }

    // allows us to specify a different number of output cards from the user's settings
    if (forcedOutputCards) {
      params.outputCards = forcedOutputCards;
    }

    if (propsRef.current.memory && propsRef.current.memoryEnabled && propsRef.current.memory.length > 0) {
      params.memory = propsRef.current.memory;
    }

    params.guidance = guidance;
    params.sessionId = sessionId;

    setMergedState({
      guidances: null,
    });

    const source = params.guidance ? "guidedWormhole" : "multiverse";

    if (constructOnly) return params;

    // Create the placeholder empty card
    let cardModel = new Card(
      null,
      {
        // TODO: make guided
        source: source,
        createdAt: new Date(),
        prompt: params.prompt,
        documentId: documentIdRef.current,
        content: [],
        guidance: guidance,
        userId: getUserId(),
      },
      false,
    );

    let card = {
      loading: loading,
      doneStreaming: false,
      hidden: false,
      model: cardModel,
      params: params,
      ready: ready,
    };

    prependNewCard(card);
  });

  const openFilter = useStableCallback((event, modifier, range) => {
    if (event) {
      event.preventDefault();
    }

    const blockReason = getFeatureActionBlockReason({
      subscription: propsRef.current.subscription,
      loadingDocument: loadingDocumentRef.current,
      loadingMessage: "Cannot start Filter because the editor is still loading.",
    });
    if (blockReason) {
      showError(blockReason);
      return;
    }

    const params = {};

    if (range) {
      params.prompt = range.text;
    } else {
      const selectionRange = getSelection();
      if (selectionRange.length === 0) {
        showError("Please highlight a passage to rewrite.");
        return;
      }

      params.prompt = selectionRange.text;
    }

    const precedingText = pluginConsideration(1000);
    params.preceding_text = precedingText;

    const followingText = pluginConsiderationForward(1000);
    params.following_text = followingText;

    if (countWords(params.prompt) < MIN_REWRITE_WORDS) {
      showError(`Please highlight at least ${MIN_REWRITE_WORDS} words.`);
      return;
    }

    if (countWords(params.prompt) > MAX_REWRITE_WORDS) {
      showError(`Passage is too long. There is a limit of ${MAX_REWRITE_WORDS} words.`);
      return;
    }

    let doneStreaming, ready, loading;
    if (modifier) {
      // if modifier is specified, then we automatically start streaming with that modifier
      params.modifier = modifier;
      lastRewriteModifierRef.current = modifier;
      loading = true;
      ready = true;
      doneStreaming = false;
    } else {
      loading = false;
      ready = false;
      doneStreaming = true;
      // Pre-fill the last-used modifier so reopening Rewrite remembers the selection
      if (lastRewriteModifierRef.current) {
        params.modifier = lastRewriteModifierRef.current;
      }
    }

    params.documentId = documentIdRef.current;

    let cardModel = new Card(
      null,
      {
        source: "rewrite",
        documentId: documentIdRef.current,
        prompt: params.prompt,
        modifier: params.modifier,
        preceding_text: precedingText,
        following_text: followingText,
        createdAt: new Date(),
        content: [],
        userId: getUserId(),
        crossAppSignal: CrossAppSignal.REWRITE_RUNNING,
      },
      false,
    );

    // Create the placeholder empty card
    let card = {
      loading: loading,
      doneStreaming: doneStreaming,
      hidden: false,
      model: cardModel,
      ready: ready,
      params: params,
    };

    prependNewCard(card);
  });

  const openCustomFunction = useStableCallback((source) => {
    let func = customFunctions.find((func) => {
      return func.source === source;
    });

    if (!func) {
      return showError("Invalid function.");
    }

    const blockReason = getFeatureActionBlockReason({
      subscription: propsRef.current.subscription,
      loadingDocument: loadingDocumentRef.current,
      loadingMessage: "Cannot start this function because the editor is still loading.",
    });
    if (blockReason) {
      showError(blockReason);
      return;
    }

    let params = {};

    if (getSelection() === null) {
      showError("Please highlight a passage to filter.");
      return;
    }

    params.prompt = getSelection();

    const MIN_WORDS = func.minWords;
    const MAX_WORDS = func.maxWords;

    if (countWords(params.prompt) < MIN_WORDS) {
      showError(`Please highlight at least ${MIN_WORDS} words.`);
      return;
    }

    if (countWords(params.prompt) > MAX_WORDS) {
      showError(`Passage is too long. There is a limit of ${MAX_WORDS} words.`);
      return;
    }

    params.documentId = documentIdRef.current;

    let cardModel = new Card(
      null,
      {
        source: source,
        documentId: documentIdRef.current,
        prompt: params.prompt,
        custom: true,
        createdAt: new Date(),
        content: [],
        userId: getUserId(),
      },
      false,
    );

    // Create the placeholder empty card
    let card = {
      loading: true,
      doneStreaming: false,
      hidden: false,
      model: cardModel,
      ready: true,
      params: params,
    };

    prependNewCard(card);
  });

  const fetchGuidance = useStableCallback(async (povTense) => {
    if (stateRef.current.loadingGuidances) {
      setMergedState({
        guidanceOpen: true,
      });

      return;
    }

    const blockReason = getFeatureActionBlockReason({
      subscription: propsRef.current.subscription,
      loadingDocument: loadingDocumentRef.current,
      loadingMessage: "Cannot use Write because the editor is still loading.",
    });
    if (blockReason) {
      showError(blockReason);
      return;
    }

    const switchVersion = documentSwitchVersionRef.current;
    let [consideration, gap] = wormholeConsideration(5000);

    setMergedState({
      loadingGuidances: true,
      guidanceOpen: true,
      guidances: null,
    });

    guidanceAbortControllerRef.current = new AbortController();

    try {
      const result = await Auth.fetchWithIdToken(
        "/api/ai/guidance",
        {
          prompt: consideration,
          documentId: documentIdRef.current,
          povTense: povTense,
        },
        guidanceAbortControllerRef.current.signal,
        {
          requestSource: "tiptap_guidance",
          suppressAbortSentry: true,
        },
      );

      if (documentSwitchVersionRef.current !== switchVersion) return;

      setMergedState({
        loadingGuidances: false,
        guidances: {
          texts: result.texts,
          completionId: result.completionId,
          id: result.guidanceId,
        },
      });
    } catch (e) {
      if (documentSwitchVersionRef.current !== switchVersion) return;
      setMergedState({
        loadingGuidances: false,
        guidances: null,
      });
    }
  });

  const openFeedback = useStableCallback((event) => {
    if (!propsRef.current.subscription.hasAccessToFeatures) {
      return showError("Your free trial or membership has expired or is overdue. Subscribe to access AI features.");
    }

    let prompt;

    const MIN_WORDS = 400;
    const MAX_WORDS = 30000;

    const range = getSelection();
    if (range.length > 0) {
      prompt = range.text;
      let count = countWords(prompt);
      if (count < MIN_WORDS) {
        showError(`Please highlight at least ${MIN_WORDS} words of a story to get feedback.`);
        return;
      }
      if (count > MAX_WORDS) {
        showError(`The max number of words allowed for feedback is ${MAX_WORDS}. Try highlighting a smaller section of your story.`);
        return;
      }
    } else {
      if (stateRef.current.wordCount < MIN_WORDS) {
        showError(`Please provide at least ${MIN_WORDS} words of a story to get feedback.`);
        return;
      }
      if (stateRef.current.wordCount > MAX_WORDS) {
        showError(`The max number of words allowed for feedback is ${MAX_WORDS}. Try highlighting a smaller section of your story.`);
        return;
      }

      prompt = tiptapRef.current.getText();
    }

    const params = {};

    params.prompt = prompt;
    params.words = countWords(prompt);

    params.documentId = documentIdRef.current;

    // Create the placeholder empty card
    let cardModel = new Card(
      null,
      {
        source: "analyze",
        documentId: documentIdRef.current,
        createdAt: new Date(),
        prompt: `Reading ${countWords(prompt)} words (0%)`,
        content: [],
        userId: getUserId(),
      },
      false,
    );

    let card = {
      loading: true,
      doneStreaming: false,
      hidden: false,
      model: cardModel,
      ready: true,
      params: params,
    };

    prependNewCard(card);
  });

  const setLabsOpen = useStableCallback((bool) => {
    setMergedState({ labsOpen: bool });
  });

  const setStoryToolOpen = useStableCallback((bool) => {
    setMergedState({ storyToolOpen: bool });
  });

  const setToneOpen = useStableCallback((bool) => {
    setMergedState({ toneOpen: bool });
  });

  const setModalOpen = useStableCallback((bool) => {
    setMergedState({ modalOpen: bool });
  });

  const openMyVoiceFunctions = useStableCallback((event, range, source) => {
    if (event) {
      event.preventDefault();
    }

    const scenes = propsRef.current.storyBibleStore.scenes;
    const sceneId = propsRef.current.storyBibleStore.sceneId;

    if (propsRef.current.subscription && !propsRef.current.subscription.hasAccessToFeatures) {
      return showError("Your free trial or membership has expired or is overdue. Subscribe to access AI features.");
    }

    if (loadingDocumentRef.current) {
      showError("Cannot open Description because the editor is still loading.");
      return;
    }

    let params = {};

    params.engine = scenes[sceneId]?.proseMode;

    if (!range) {
      range = getSelection();

      if (range.length === 0) {
        showError("Please highlight a word or phrase to describe.");
        return;
      } else {
        params.object = range.text;
      }
    } else {
      // range is being passed in
      params.object = range.text;
    }

    const priorConsideration = expandConsideration(500, range);
    const [consideration] = wormholeConsideration(range);
    params.passage = consideration;
    params.priorText = priorConsideration;

    params.senses = Object.keys(propsRef.current.describeSenses);

    if (stateRef.current.max_tokens) {
      params.max_tokens = stateRef.current.max_tokens;
    }

    if (stateRef.current.engine) {
      params.engine = stateRef.current.engine;
    }

    params.documentId = documentIdRef.current;
    params.creativity = scenes[sceneId]?.creativityLevel;

    let cardModel = new Card(
      null,
      {
        source: source,
        documentId: documentIdRef.current,
        object: params.object,
        passage: params.passage,
        senses: params.senses,
        createdAt: new Date(),
        content: [],
        userId: getUserId(),
        engine: params.engine,
        creativity: params.creativity,
        priorText: priorConsideration,
      },
      false,
    );

    // Create the placeholder empty card
    let card = {
      loading: true,
      doneStreaming: false,
      hidden: false,
      model: cardModel,
      params: params,
      ready: true,
    };

    prependNewCard(card);
  });

  const openDescription = useStableCallback((event, range) => {
    if (event) {
      event.preventDefault();
    }

    if (propsRef.current.subscription && !propsRef.current.subscription.hasAccessToFeatures) {
      return showError("Your free trial or membership has expired or is overdue. Subscribe to access AI features.");
    }

    if (loadingDocumentRef.current) {
      showError("Cannot open Description because the editor is still loading.");
      return;
    }

    let params = {};

    if (!range) {
      range = getSelection();

      if (range.length === 0) {
        showError("Please highlight a word or phrase to describe.");
        return;
      } else {
        params.object = range.text;
      }
    } else {
      // range is being passed in
      params.object = range.text;
    }

    params.passage = descriptionConsideration(range);

    params.senses = Object.keys(propsRef.current.describeSenses);

    const url = "/api/ai/description";

    if (stateRef.current.max_tokens) {
      params.max_tokens = stateRef.current.max_tokens;
    }

    if (stateRef.current.engine) {
      params.engine = stateRef.current.engine;
    }

    params.documentId = documentIdRef.current;

    let cardModel = new Card(
      null,
      {
        source: "description",
        documentId: documentIdRef.current,
        object: params.object,
        passage: params.passage,
        senses: params.senses,
        createdAt: new Date(),
        content: {},
        userId: getUserId(),
        crossAppSignal: CrossAppSignal.DESCRIBE_RUNNING,
      },
      false,
    );

    // Create the placeholder empty card
    let card = {
      loading: true,
      doneStreaming: false,
      hidden: false,
      model: cardModel,
      params: params,
      ready: true,
    };

    prependNewCard(card);
  });

  const openVisualize = useStableCallback((event, range) => {
    if (event) {
      event.preventDefault();
    }

    if (propsRef.current.subscription && !propsRef.current.subscription.hasAccessToFeatures) {
      return showError("Your free trial or membership has expired or is overdue. Subscribe to access AI features.");
    }

    if (loadingDocumentRef.current) {
      showError("Cannot open Description because the editor is still loading.");
      return;
    }

    const params = {};

    if (range) {
      params.prompt = range.text;
    } else {
      const prompt = getSelection().text;

      if (countWords(prompt) < MIN_VISUALIZE_WORDS) {
        return showError(`You must highlight at least ${MIN_VISUALIZE_WORDS} words to use Visualize`);
      }

      params.prompt = prompt;
    }

    params.source = "visualize";

    if (stateRef.current.max_tokens) {
      params.max_tokens = stateRef.current.max_tokens;
    }

    if (stateRef.current.engine) {
      params.engine = stateRef.current.engine;
    }

    params.documentId = documentIdRef.current;

    // Send projectId so backend can load characters directly from DB
    if (propsRef.current.storyBibleStore.isActive && propsRef.current.project?.id) {
      params.projectId = propsRef.current.project.id;
    }

    let cardModel = new Card(
      null,
      {
        source: "visualize",
        documentId: documentIdRef.current,
        object: params.object,
        prompt: params.prompt,
        createdAt: new Date(),
        content: [],
        userId: getUserId(),
      },
      false,
    );

    // Create the placeholder empty card
    let card = {
      loading: true,
      doneStreaming: false,
      hidden: false,
      model: cardModel,
      params: params,
      ready: true,
    };

    prependNewCard(card);
  });

  const openRelatedWords = useStableCallback((range) => {
    let word, paragraph, relativeIndex;

    if (range.length === 0) {
      return showError("Please highlight a word or phrase to get related words.");
    }
    word = range.text.trim();

    paragraph = getSelectedParagraph(tiptapRef.current, range);
    relativeIndex = paragraph.indexOf(word);

    const cachedStateRelWords = stateRef.current.showRelatedWords;
    const showRelWordsObj = {
      word: word,
      paragraph: paragraph,
      range: range,
      relativeIndex: relativeIndex,
    };

    // check in state to see if showRelatedWordsObject is the same
    if (cachedStateRelWords) {
      if (
        cachedStateRelWords.word === showRelWordsObj.word &&
        cachedStateRelWords.paragraph === showRelWordsObj.paragraph &&
        cachedStateRelWords.relativeIndex === showRelWordsObj.relativeIndex &&
        cachedStateRelWords.range === showRelWordsObj.range
      ) {
        return;
      }
    }

    setMergedState({ showRelatedWords: showRelWordsObj });
  });

  const insertRelatedWord = useStableCallback((word, range, selection = true) => {
    replaceSelection(tiptapRef.current, word, selection === false ? range : undefined);

    if (stateRef.current.showRelatedWords) {
      setMergedState({ showRelatedWords: false });
    }
  });

  // Returns prosemirror range conformed to former editors range object:
  //   range.index = current cursor position
  //   range.length = length of the selection (0 if nothing selected)
  // Note that PM will always return a selection even if the editor is not in focus
  // at the start, it returns the end of the doc
  // and thereafter, it returns the last known cursor position
  // When the editor is blurred with a persisted (fake) selection highlight,
  // that range is the selection of record.
  const getSelection = useStableCallback(() => {
    const tiptap = tiptapRef.current;
    const effective = getEffectiveSelection(tiptap);

    if (!effective) {
      return {
        length: 0,
        index: tiptap.view.state.selection.$from.pos,
        text: "",
      };
    }

    return {
      length: effective.to - effective.from,
      index: effective.from,
      text: effective.text,
    };
  });

  const openExpand = useStableCallback((event, range) => {
    if (event) {
      event.preventDefault();
    }

    if (propsRef.current.subscription && !propsRef.current.subscription.hasAccessToFeatures) {
      return showError("Your free trial or membership has expired or is overdue. Subscribe to access AI features.");
    }

    if (loadingDocumentRef.current) {
      showError("Cannot start Expand because the editor is still loading.");
      return;
    }

    const plugin = propsRef.current.sudowriteStore.installedPlugins.find((p) => p.plugin.id === "documentPlugins/expand/muse")?.plugin;
    if (!plugin) return;

    const inputDocumentBlock = plugin.blocks.find((block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT);

    if (!inputDocumentBlock) return;

    runDocumentPluginAction(inputDocumentBlock, plugin, documentPluginBridge, true, {}, undefined, undefined, range);
    return;
  });

  // Inserts HTML, text, or an image into the editor. Text insertions normalize
  // newlines to preserve expected paragraph spacing and the selection context.
  const insertEditor = useStableCallback(({ text, html, image, colored = true, draftAppend = false }) => {
    const editor = tiptapRef.current;
    if (!editor || editor.isDestroyed) return;

    if (html && html.trim()) {
      disableInsertUnmarkingRef.current = true;
      editor.chain().focus().scrollIntoView().run();

      // Parse the source HTML before stamping the inserted mark onto its text.
      // This keeps hardBreak nodes bare (matching Copy -> Paste) while content
      // and marks still land in one history step.
      const processedHtml = convertQuotesInHtml(html, editor);
      const toPos = insertCardHtml(editor, processedHtml, colored);

      // Collapse to a caret at the end of the inserted content.
      let tr = editor.state.tr;
      let newSelection = TextSelection.create(tr.doc, toPos);
      tr.setSelection(newSelection);
      editor.view.dispatch(tr);

      disableInsertUnmarkingRef.current = false;
      return;
    }

    if (text) {
      disableInsertUnmarkingRef.current = true;

      let normalizedText = text;
      // Normalize newlines and trim leading/trailing blank lines
      normalizedText = normalizedText
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .replace(/^\s*\n+/, "")
        .replace(/\n+\s*$/, "");
      // replace double tabs with one
      normalizedText = normalizedText.replace(/\t\s*\t/g, "\t");

      // Convert straight quotes to match document style (defaults to curly)
      normalizedText = convertQuotes(normalizedText, editor);

      editor.chain().focus().scrollIntoView().run();

      const lines = normalizedText.split("\n");
      const indentEnabled = !!editorRef?.current?.classList?.contains("indented");
      const { state } = editor;
      const sel = state.selection;
      const $from = sel.$from;
      const parent = $from.parent;
      const parentIsParagraph = parent && parent.type && parent.type.name === "paragraph";
      const parentTextLen = (parent?.textContent || "").length;
      const selectionNotEmpty = !sel.empty;
      const inlineContext = parentIsParagraph && (parentTextLen > 0 || selectionNotEmpty);
      const newLineContext = sel.empty && parentIsParagraph && parentTextLen === 0;

      let toPos;

      const hasDouble = /\n\n+/.test(normalizedText);

      // Pre-compute marks to bake into text nodes so insertion + mark are one history step.
      const pmMarks = colored ? [editor.schema.marks.inserted.create()] : null;

      if (draftAppend && terminalDraftListEnd(state) !== null) {
        const paragraphs = normalizedText.split(/\n\n+/).map((block) => {
          const inlineNodes = buildPmInlineNodesFromLines(editor, block.split("\n"), pmMarks);
          return editor.schema.nodes.paragraph.create(null, inlineNodes);
        });
        const appendTr = buildDraftListAppendTransaction(state, paragraphs);
        if (appendTr) {
          editor.view.dispatch(appendTr);
          disableInsertUnmarkingRef.current = false;
          return;
        }
      }

      if (!inlineContext && ((indentEnabled && lines.length > 1) || hasDouble)) {
        // Block context: insert structured paragraphs. Blank-line runs are
        // plain paragraph boundaries — no spacer empty paragraphs; visual
        // separation comes from the Paragraph Spacing setting (matches paste).
        const blocks = normalizedText.split(/\n\n+/);
        const nodes = [];
        for (let b = 0; b < blocks.length; b++) {
          const block = blocks[b];
          const blockLines = block.split("\n");
          const inlineNodes = buildPmInlineNodesFromLines(editor, blockLines, pmMarks);
          nodes.push(editor.schema.nodes.paragraph.create(null, inlineNodes.length ? inlineNodes : null));
        }

        // Replace selection with a slice of node instances to ensure insertion in all contexts
        let fragment = Fragment.fromArray(nodes);
        let slice = new Slice(fragment, 0, 0);
        let tr = editor.state.tr;
        tr.replaceSelection(slice);
        toPos = tr.selection.from;
        editor.view.dispatch(tr);
      } else if (inlineContext && hasDouble) {
        const blocks = normalizedText.split(/\n\n+/);

        // Build ALL content in a single transaction: first block inline, rest as paragraphs
        const firstLines = blocks[0].split("\n");
        const firstInlineNodes = buildPmInlineNodesFromLines(editor, firstLines, pmMarks);
        let fragment = Fragment.fromArray(firstInlineNodes);
        let slice = new Slice(fragment, 0, 0);
        let tr = editor.state.tr;
        tr.replaceSelection(slice);

        // Append remaining blocks as paragraphs without spacer empty paragraphs.
        if (blocks.length > 1) {
          const restParagraphs = [];
          for (let b = 1; b < blocks.length; b++) {
            const block = blocks[b];
            const blockLines = block.split("\n");
            const inlineNodes = buildPmInlineNodesFromLines(editor, blockLines, pmMarks);
            restParagraphs.push(editor.schema.nodes.paragraph.create(null, inlineNodes.length ? inlineNodes : null));
          }
          const restFragment = Fragment.fromArray(restParagraphs);
          // Insert at current cursor position (end of first inline insert)
          const insertPos = tr.selection.from;
          tr.insert(insertPos, restFragment);
        }

        toPos = tr.selection.from;
        editor.view.dispatch(tr);
      } else if (newLineContext) {
        // In an empty paragraph: insert using command chain to ensure dispatch
        if (hasDouble) {
          const blocks = normalizedText.split(/\n\n+/);
          const nodes = [];
          for (let b = 0; b < blocks.length; b++) {
            const block = blocks[b];
            const blockLines = block.split("\n");
            const inlineNodes = buildPmInlineNodesFromLines(editor, blockLines, pmMarks);
            nodes.push(editor.schema.nodes.paragraph.create(null, inlineNodes.length ? inlineNodes : null));
          }
          editor.chain().focus().insertContent(nodes).run();
        } else {
          const inlineNodes = buildPmInlineNodesFromLines(editor, lines, pmMarks);
          const slice = new Slice(Fragment.fromArray(inlineNodes), 0, 0);
          const tr = editor.state.tr;
          tr.replaceSelection(slice);
          editor.view.dispatch(tr);
        }
        toPos = editor.state.selection.from;
      } else {
        // Inline context or non-indented: insert inline (text + hardBreaks), no block insertion
        const inlineNodes = buildPmInlineNodesFromLines(editor, lines, pmMarks);

        let fragment = Fragment.fromArray(inlineNodes);
        let slice = new Slice(fragment, 0, 0);

        let tr = editor.state.tr;
        tr.replaceSelection(slice);
        toPos = tr.selection.from;
        editor.view.dispatch(tr);
      }

      // Collapse to a caret after insert so we don't leave a highlighted selection range.
      let tr = editor.state.tr;
      let newSelection = TextSelection.create(tr.doc, toPos);
      tr.setSelection(newSelection);
      editor.view.dispatch(tr);

      disableInsertUnmarkingRef.current = false;
    } else {
      tiptapRef.current.chain().setInserted().focus().scrollIntoView().setImage({ src: image }).run();
      // The editor container grows with its content; no manual resize needed.
    }
  });

  const clearError = useStableCallback(() => {
    setMergedState({ error: null });
  });

  const showError = useStableCallback((m, timeout = 4000) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    setMergedState({ error: m }, () => {
      errorTimeoutRef.current = setTimeout(() => {
        setMergedState({ error: null });
        errorTimeoutRef.current = null;
      }, timeout);
    });
  });

  const showMessage = useStableCallback((m, time = 3000) => {
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    setMergedState({ message: m }, () => {
      messageTimeoutRef.current = setTimeout(() => {
        setMergedState({ message: null });
        messageTimeoutRef.current = null;
      }, time);
    });
  });

  const cleanupTiptapDocument = useStableCallback(() => {
    componentUnmountedRef.current = true;
    documentSwitchVersionRef.current++;
    setupEditorStartedRef.current = false;
    // Clear all timeouts
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    if (commandModeIntervalRef.current) {
      clearInterval(commandModeIntervalRef.current);
      commandModeIntervalRef.current = null;
    }
    if (containerLookupRetryTimeoutRef.current) {
      clearTimeout(containerLookupRetryTimeoutRef.current);
      containerLookupRetryTimeoutRef.current = null;
    }
    if (placeholderRafIdRef.current) {
      cancelAnimationFrame(placeholderRafIdRef.current);
      placeholderRafIdRef.current = null;
    }

    // Remove window event listeners
    window.removeEventListener("beforeunload", beforeUnloadWarning);
    window.removeEventListener("resize", onWindowResize);
    window.removeEventListener("resize", onResizePreserveScroll);
    window.document.removeEventListener("keydown", onGlobalKeyPressed);

    // Remove scroll listener
    innerRef.current?.removeEventListener("scroll", handleScroll, true);

    // Remove editor-container event listeners
    if (editorContainerRef.current) {
      editorContainerRef.current.removeEventListener("click", handleEditorClickRef.current);
      editorContainerRef.current.removeEventListener("mousedown", handleEditorMousedownRef.current);
      editorContainerRef.current.removeEventListener("keydown", onEditorHistoryKeyDownCapture, true);
      editorContainerRef.current.removeEventListener("keydown", onKeyPressed);
      if (handlePointerDownRef.current) {
        editorContainerRef.current.removeEventListener("pointerdown", handlePointerDownRef.current, true);
      }
      if (handlePointerUpRef.current) {
        editorContainerRef.current.removeEventListener("pointerup", handlePointerUpRef.current, true);
      }
    }
    if (handlePointerDragEndRef.current) {
      window.removeEventListener("pointerup", handlePointerDragEndRef.current);
      window.removeEventListener("pointercancel", handlePointerDragEndRef.current);
    }
    if (onAgentHighlightDismissRef.current) {
      window.document.removeEventListener("mousedown", onAgentHighlightDismissRef.current, true);
    }

    // Clean up tiptap event listeners
    if (tiptapRef.current) {
      if (onAgentYSyncTransactionRef.current) {
        tiptapRef.current.off("transaction", onAgentYSyncTransactionRef.current);
      }
      tiptapRef.current.off("update", onTextChange);
      tiptapRef.current.off("selectionUpdate", onSelectionChange);
    }

    if (agentManuscriptWindowListenersBoundRef.current && onAgentEditDocumentStartRef.current && onAgentHighlightEditRef.current) {
      window.removeEventListener("sudo-agent-edit-document-start", onAgentEditDocumentStartRef.current);
      window.removeEventListener("sudo-agent-highlight-edit", onAgentHighlightEditRef.current);
      if (onAgentApplyManuscriptReplacementRef.current) {
        window.removeEventListener(
          "sudo-agent-apply-manuscript-replacement",
          onAgentApplyManuscriptReplacementRef.current,
        );
      }
      if (onAgentEditDocumentRecordUndoRef.current) {
        window.removeEventListener("sudo-agent-edit-document-record-undo", onAgentEditDocumentRecordUndoRef.current);
      }
      if (onAgentManuscriptUndoRef.current) {
        window.removeEventListener("sudo-agent-manuscript-undo", onAgentManuscriptUndoRef.current);
      }
      agentManuscriptWindowListenersBoundRef.current = false;
    }

    // Cancel debounced functions
    if (onSelectionChange?.cancel) {
      onSelectionChange.cancel();
    }
    if (updateSelectionIndex?.cancel) {
      updateSelectionIndex.cancel();
    }
    if (generateDocumentTitle?.cancel) {
      generateDocumentTitle.cancel();
    }
    if (updatesOnTextChange?.cancel) {
      updatesOnTextChange.cancel();
    }

    // Abort any pending requests
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    if (guidanceAbortControllerRef.current) {
      guidanceAbortControllerRef.current.abort();
    }

    if (documentModelRef.current) {
      documentModelRef.current.release();
    }

    if (editorModelRef.current) {
      destroyEditorModel(editorModelRef.current);
    }
    useProjectStore.getState().setManuscriptEditorGetter(null);
    documentModelRef.current = null;
    editorModelRef.current = null;
    tiptapRef.current = null;
    relatedWordsStoreRef.current = null;
    favoritesStoreRef.current = null;
    loadingDocumentRef.current = false;
  });

  const cleanupForDocumentSwitch = useStableCallback(() => {
    documentSwitchVersionRef.current++;

    usePersistentChatStore.getState().resetForDocumentSwitch();

    // Scroll to top when switching documents
    const scrollContainer = centerColumnRef?.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "auto" });
    }

    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = new AbortController();
    }
    if (guidanceAbortControllerRef.current) {
      guidanceAbortControllerRef.current.abort();
    }

    if (tiptapRef.current) {
      if (onAgentYSyncTransactionRef.current) {
        tiptapRef.current.off("transaction", onAgentYSyncTransactionRef.current);
      }
      tiptapRef.current.off("update", onTextChange);
      tiptapRef.current.off("selectionUpdate", onSelectionChange);
    }

    onSelectionChange?.cancel?.();
    updateSelectionIndex?.cancel?.();
    generateDocumentTitle?.cancel?.();
    updatesOnTextChange?.cancel?.();

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    if (commandModeIntervalRef.current) {
      clearInterval(commandModeIntervalRef.current);
      commandModeIntervalRef.current = null;
    }

    if (documentModelRef.current) {
      documentModelRef.current.release();
    }

    if (editorModelRef.current) {
      destroyEditorModel(editorModelRef.current);
    }
    useProjectStore.getState().setManuscriptEditorGetter(null);

    if (onAgentHighlightDismissRef.current) {
      window.document.removeEventListener("mousedown", onAgentHighlightDismissRef.current, true);
    }

    documentIdRef.current = null;
    documentModelRef.current = null;
    editorModelRef.current = null;
    tiptapRef.current = null;
    relatedWordsStoreRef.current = null;
    favoritesStoreRef.current = null;
    cardQueriesRef.current = {};
    commentsManager.resetMarks();

    lastKnownCursorPositionRef.current = null;
    disableInsertUnmarkingRef.current = false;
    agentEditUndoStackRef.current = [];
    agentEditRedoStackRef.current = [];
    pendingAgentEditUndoSnapshotRef.current = null;
    pendingAgentEditUndoDepthRef.current = null;
    nativeUndoRedoSnapshotsRef.current = [];
    editorHistoryMutationRef.current = false;
    agentEditUndoCapturedRef.current = false;
    loadingDocumentRef.current = false;
    submittedRef.current = false;
    considerationRef.current = null;
    startRef.current = null;
  });

  const reinitializeForDocument = useStableCallback(async () => {
    const document = propsRef.current.document;
    if (!document) {
      throw new Error("Document prop is required");
    }

    const switchVersion = documentSwitchVersionRef.current;
    documentIdRef.current = document.id;
    relatedWordsStoreRef.current = new RelatedWordsStore({ documentId: documentIdRef.current });

    setMergedState(getDocumentSwitchStateReset());

    loadingDocumentRef.current = true;

    const editorHandlers = {
      annotations: {
        onHover: (annotationIds) => {
          propsRef.current.commentStore.setHoveredAnnotationIds(annotationIds);
        },
        onLeave: (_annotationIds) => {
          propsRef.current.commentStore.setHoveredAnnotationIds([]);
        },
        onMarkRemoved: (annotationId) => {
          // Agent undo/redo can replace the Y.Doc outside native history. Its existing
          // replay guard covers that replacement until local editor verification finishes.
          if (editorHistoryMutationRef.current) return;
          void propsRef.current.commentStore.resolveCommentWhenAnnotationRemoved(annotationId);
        },
        commentsManager,
      },
    };

    let editorForDocument = null;
    editorForDocument = new Editor({
      documentId: documentIdRef.current,
      user: propsRef.current.user,
      canWriteChecklists: () => !!propsRef.current.subscription?.hasAccessToChecklists,
      showError: (msg) => showError(msg),
      onSynced: () => {
        if (!isCurrentEditorSetup(editorForDocument, switchVersion)) {
          addStaleEditorSetupBreadcrumb("reinitialize:onSynced", editorForDocument, switchVersion);
          return;
        }

        const tiptap = setEditorModelEditable(editorForDocument, true, "reinitialize:onSynced", switchVersion);
        if (!tiptap) return;
        /*
         * This is the reinitialization/document-switch sync path. Only after the existing
         * current-editor guards pass do we declare the manuscript annotation index authoritative.
         * Before provider sync, an empty local editor is not evidence that all open comments should
         * disappear.
         */
        propsRef.current.commentStore.refreshDocumentAnnotationIndex({ markReady: true });
        propsRef.current.setSaved("saved");

        // Restore spell + grammar state from persisted user data. Both kinds
        // are driven from the same TipTap extension; we set the documentId
        // once so the server can scope user_style_rules.
        if (propsRef.current.documentId && tiptap?.commands?.setGrammarDocumentId) {
          tiptap.commands.setGrammarDocumentId(propsRef.current.documentId);
        }
        if (propsRef.current.editorOptions?.spellCheckEnabled && tiptap?.commands?.setSpellEnabled) {
          tiptap.commands.setSpellEnabled(true);
        }
        if (propsRef.current.editorOptions?.grammarCheckEnabled && tiptap?.commands?.setGrammarEnabled) {
          tiptap.commands.setGrammarEnabled(true);
        }
        if (propsRef.current.editorOptions?.languagePreference && tiptap?.commands?.setLanguagePreference) {
          tiptap.commands.setLanguagePreference(propsRef.current.editorOptions.languagePreference);
        }

        if (
          shouldShowLinkingMenuOnEditorSynced({
            textLength: tiptap.getText().length,
            existingCardsLoaded: stateRef.current.existingCardsLoaded,
            newCardsLength: stateRef.current.newCards.length,
            existingCardsLength: stateRef.current.existingCards.length,
            isCreatingNewBlankDocument: propsRef.current.projectStore.isCreatingNewBlankDocument,
            proseGeneratorActive: propsRef.current.storyBibleStore.proseGeneratorActive,
          })
        ) {
          propsRef.current.projectStore.setShowLinkingMenu(true);
        }
        propsRef.current.projectStore.setIsCreatingNewBlankDocument(false);

        setMergedState({ message: null, editorReady: true });
        loadingDocumentRef.current = false;
        maybeStartParagraphSpacingTour(tiptap);
        void import("src/components/PersistentChat/navigateToManuscriptHighlightFromChat").then(
          ({ flushPendingManuscriptHighlightNavigation }) => {
            flushPendingManuscriptHighlightNavigation({ finalizeOnNotFound: true });
          },
        );
      },
      setSaved: propsRef.current.setSaved,
      editorHandlers,
    });
    editorModelRef.current = editorForDocument;

    documentModelRef.current = document;
    setMergedState({ documentReady: true });

    await editorForDocument.init();

    // init() returns without creating tiptap if the editor was destroyed while waiting
    // for IndexedDB. A fast document switch can hit that path, so stop before touching
    // tiptapRef/current editor wiring unless this setup is still current.
    if (!isCurrentEditorSetup(editorForDocument, switchVersion)) {
      addStaleEditorSetupBreadcrumb("reinitialize:afterInit", editorForDocument, switchVersion);
      destroyEditorModel(editorForDocument);
      return;
    }

    tiptapRef.current = editorForDocument.tiptap;
    /*
     * Reinitialization can attach an editor whose provider has already synced. Pass that fact into
     * the store so it indexes immediately rather than waiting for a synced callback that may
     * already have fired. A manuscript loaded from IndexedDB is authoritative locally too,
     * including an empty paragraph while offline; a fresh empty cache is not.
     */
    propsRef.current.commentStore.setTiptapEditor(tiptapRef.current, {
      documentSynced: editorForDocument.provider?.synced === true || editorForDocument.localDocumentLoaded === true,
    });
    window.tiptap = tiptapRef.current;
    useProjectStore.getState().setManuscriptEditorGetter(() => editorModelRef.current);
    void import("src/components/PersistentChat/navigateToManuscriptHighlightFromChat").then(
      ({ flushPendingManuscriptHighlightNavigation }) => {
        flushPendingManuscriptHighlightNavigation();
      },
    );

    tiptapRef.current?.setEditable?.(false);

    editorRef.current = window.document.getElementById("editor");

    if (editorRef.current) {
      editorRef.current.innerHTML = "";
      editorRef.current.append(tiptapRef.current.options.element);
    }

    tiptapRef.current.on("update", onTextChange);
    tiptapRef.current.on("selectionUpdate", onSelectionChange);
    bindAgentManuscriptEditHandlers();

    favoritesStoreRef.current = new FavoritesStore(getUserId(), documentIdRef.current);
    favoritesStoreRef.current.initialize().then(() => {
      if (documentSwitchVersionRef.current !== switchVersion) return;
      if (favoritesStoreRef.current) {
        setMergedState({
          favorites: favoritesStoreRef.current.getSortedFavorites(),
          favoritesByCards: favoritesStoreRef.current.getFavoritesByCards(),
        });
      }
    });

    getCards(stride, null, switchVersion);
    updateWordCount();
  });

  // Sets up all stories, side effects, etc necessary for editor
  const setupEditor = useStableCallback(async () => {
    const switchVersion = documentSwitchVersionRef.current;
    window.addEventListener("beforeunload", beforeUnloadWarning);

    // get document from props
    const document = propsRef.current.document;
    if (!document) {
      throw new Error("Document prop is required");
    }

    // get document id from document prop
    documentIdRef.current = document.id;

    relatedWordsStoreRef.current = new RelatedWordsStore({ documentId: documentIdRef.current });

    const handleHTMLDocumentMouseDown = (event) => {
      if (event.target?.closest?.("#highlight-menu")) {
        return;
      }
      setMergedState({ shouldAutoScroll: false });
    };

    const handlePointerDown = (event) => {
      pointerDownPosition.current = { x: event.clientX, y: event.clientY };
      if (event.pointerType === 'mouse') {
        setIsPointerDragging(true);
      }
    };

    const handlePointerUp = (event) => {
      pointerUpPosition.current = { x: event.clientX, y: event.clientY };
    };

    const handlePointerDragEnd = () => {
      setIsPointerDragging(false);
    };

    // Editor clicks bubble to the main document now (no iframe), so a single
    // document-level mousedown listener covers both surfaces.
    window.document.addEventListener("mousedown", handleHTMLDocumentMouseDown, {
      passive: true,
      capture: true,
    });

    // Track pointer start/end inside the editor for highlight-menu drag direction.
    if (handlePointerDownRef.current) {
      editorContainerRef.current?.removeEventListener("pointerdown", handlePointerDownRef.current, true);
    }
    if (handlePointerUpRef.current) {
      editorContainerRef.current?.removeEventListener("pointerup", handlePointerUpRef.current, true);
    }
    handlePointerDownRef.current = handlePointerDown;
    handlePointerUpRef.current = handlePointerUp;
    editorContainerRef.current?.addEventListener("pointerdown", handlePointerDown, {
      passive: true,
      capture: true,
    });
    editorContainerRef.current?.addEventListener("pointerup", handlePointerUp, {
      passive: true,
      capture: true,
    });

    // Clear pointer-dragging state on window pointerup/pointercancel so the
    // highlight menu can appear even when the pointer is released outside the editor.
    if (handlePointerDragEndRef.current) {
      window.removeEventListener("pointerup", handlePointerDragEndRef.current);
      window.removeEventListener("pointercancel", handlePointerDragEndRef.current);
    }
    handlePointerDragEndRef.current = handlePointerDragEnd;
    window.addEventListener("pointerup", handlePointerDragEnd);
    window.addEventListener("pointercancel", handlePointerDragEnd);

    // For hovering over annotations
    const editorHandlers = {
      annotations: {
        onHover: (annotationIds) => {
          propsRef.current.commentStore.setHoveredAnnotationIds(annotationIds);
        },
        onLeave: (_annotationIds) => {
          propsRef.current.commentStore.setHoveredAnnotationIds([]);
        },
        onMarkRemoved: (annotationId) => {
          // Agent undo/redo can replace the Y.Doc outside native history. Its existing
          // replay guard covers that replacement until local editor verification finishes.
          if (editorHistoryMutationRef.current) return;
          void propsRef.current.commentStore.resolveCommentWhenAnnotationRemoved(annotationId);
        },
        commentsManager,
      },
    };

    let editorReady = false;
    let documentReady = false;

    // This function is called when both the editor and document are ready.
    // Both these components are initialized in parallel to speed up the loading process.
    const afterEditorAndDocumentReady = () => {
      if (editorReady && documentReady) {
        loadingDocumentRef.current = false;
      }
    };

    loadingDocumentRef.current = true;

    /**
     * Initialize the editor, which handles loading the tiptap editor
     * and initializing hocuspocus websocket connection.
     */
    let editorForSetup = null;
    editorForSetup = new Editor({
      documentId: documentIdRef.current,
      user: propsRef.current.user,
      canWriteChecklists: () => !!propsRef.current.subscription?.hasAccessToChecklists,
      showError: (msg) => showError(msg),
      onSynced: () => {
        if (!isCurrentEditorSetup(editorForSetup, switchVersion)) {
          addStaleEditorSetupBreadcrumb("setup:onSynced", editorForSetup, switchVersion);
          return;
        }

        const tiptap = setEditorModelEditable(editorForSetup, true, "setup:onSynced", switchVersion);
        if (!tiptap) return;
        /*
         * The initial setup path needs the same readiness transition as reinitialization. Wiring only
         * document switches would leave first-load filtering in its fallback state; wiring only first
         * load would miss navigation/rebuilds. Existing stale-setup checks remain in force.
         */
        propsRef.current.commentStore.refreshDocumentAnnotationIndex({ markReady: true });
        propsRef.current.setSaved("saved");

        // Restore spell + grammar state from persisted user data.
        if (propsRef.current.documentId && tiptap?.commands?.setGrammarDocumentId) {
          tiptap.commands.setGrammarDocumentId(propsRef.current.documentId);
        }
        if (propsRef.current.editorOptions?.spellCheckEnabled && tiptap?.commands?.setSpellEnabled) {
          tiptap.commands.setSpellEnabled(true);
        }
        if (propsRef.current.editorOptions?.grammarCheckEnabled && tiptap?.commands?.setGrammarEnabled) {
          tiptap.commands.setGrammarEnabled(true);
        }
        if (propsRef.current.editorOptions?.languagePreference && tiptap?.commands?.setLanguagePreference) {
          tiptap.commands.setLanguagePreference(propsRef.current.editorOptions.languagePreference);
        }

        // if the document is empty, has no cards, and isn't a new blank document, and the prose generator is not active,show the linking menu
        if (
          shouldShowLinkingMenuOnEditorSynced({
            textLength: tiptap.getText().length,
            existingCardsLoaded: stateRef.current.existingCardsLoaded,
            newCardsLength: stateRef.current.newCards.length,
            existingCardsLength: stateRef.current.existingCards.length,
            isCreatingNewBlankDocument: propsRef.current.projectStore.isCreatingNewBlankDocument,
            proseGeneratorActive: propsRef.current.storyBibleStore.proseGeneratorActive,
          })
        ) {
          propsRef.current.projectStore.setShowLinkingMenu(true);
        }
        // reset the flag for creating a new blank document
        propsRef.current.projectStore.setIsCreatingNewBlankDocument(false);

        setMergedState({
          message: null,
        });
        // NOTE: pre-existing no-op (listener was added as mousedown+capture);
        // kept verbatim so the always-on auto-scroll-stop behavior is unchanged.
        window.document.removeEventListener("click", handleHTMLDocumentMouseDown);

        editorReady = true;
        afterEditorAndDocumentReady();
        setMergedState({ editorReady: true });
        maybeStartParagraphSpacingTour(tiptap);
        void import("src/components/PersistentChat/navigateToManuscriptHighlightFromChat").then(
          ({ flushPendingManuscriptHighlightNavigation }) => {
            flushPendingManuscriptHighlightNavigation({ finalizeOnNotFound: true });
          },
        );
      },
      setSaved: propsRef.current.setSaved,
      editorHandlers,
    });
    editorModelRef.current = editorForSetup;

    /**
     * Use document from props (already initialized in the store)
     */
    documentModelRef.current = document;

    // Document is already initialized when passed as prop, mark as ready immediately
    documentReady = true;
    afterEditorAndDocumentReady();
    setMergedState({ documentReady: true });

    await editorForSetup.init(); // intialize the editor

    // The initial mount path can race with an immediate document switch from agentic
    // chat actions. Do not let the older setup reset refs or flags for the newer editor.
    if (!isCurrentEditorSetup(editorForSetup, switchVersion)) {
      addStaleEditorSetupBreadcrumb("setup:afterInit", editorForSetup, switchVersion);
      const wasCurrentEditor = editorModelRef.current === editorForSetup;
      destroyEditorModel(editorForSetup);
      if (wasCurrentEditor) {
        setupEditorStartedRef.current = false;
      }
      return;
    }

    tiptapRef.current = editorForSetup.tiptap;
    /*
     * Close the already-synced attachment race during initial setup too. Both attachment sites
     * reset the store's editor/index, so both must explicitly carry provider readiness; this is
     * symmetric lifecycle wiring, not a second source of annotation data.
     */
    propsRef.current.commentStore.setTiptapEditor(tiptapRef.current, {
      documentSynced: editorForSetup.provider?.synced === true || editorForSetup.localDocumentLoaded === true,
    });
    window.tiptap = tiptapRef.current;
    useProjectStore.getState().setManuscriptEditorGetter(() => editorModelRef.current);
    void import("src/components/PersistentChat/navigateToManuscriptHighlightFromChat").then(
      ({ flushPendingManuscriptHighlightNavigation }) => {
        flushPendingManuscriptHighlightNavigation();
      },
    );

    tiptapRef.current?.setEditable?.(false); // set not editable until provider has synced

    editorRef.current = window.document.getElementById("editor");

    if (!editorRef.current) {
      Sentry.captureMessage("Tiptap setupEditor: #editor mount point missing after init — retrying", {
        level: "warning",
        tags: { documentId: documentIdRef.current, feature: "tiptap_setup_editor" },
      });
      editorContainerRef.current?.removeEventListener("click", handleEditorClickRef.current);
      destroyEditorModel(editorForSetup);
      setupEditorStartedRef.current = false;
      setMergedState({ documentReady: false, editorReady: false });
      requestAnimationFrame(() => {
        if (!componentUnmountedRef.current) {
          tryStartSetupEditor();
        }
      });
      return;
    }

    editorRef.current.append(tiptapRef.current.options.element);

    if (editorContainerRef.current) {
      // Editor-scoped key handling: captures keystrokes anywhere in the editor
      // container (typing or not). onGlobalKeyPressed skips editor-origin events
      // so shortcuts like Cmd+K/Cmd+F never double-fire.
      editorContainerRef.current.removeEventListener("keydown", onEditorHistoryKeyDownCapture, true);
      editorContainerRef.current.addEventListener("keydown", onEditorHistoryKeyDownCapture, true);
      editorContainerRef.current.removeEventListener("keydown", onKeyPressed);
      editorContainerRef.current.addEventListener("keydown", onKeyPressed);
    }

    window.document.addEventListener("keydown", onGlobalKeyPressed);

    tiptapRef.current.on("update", onTextChange);
    tiptapRef.current.on("selectionUpdate", onSelectionChange);
    bindAgentManuscriptEditHandlers();

    // Close modals on clicking inside the editor
    handleEditorMousedownRef.current = (e) => {
      propsRef.current.setSettingsOpen(false);
      propsRef.current.setHelpOpen(false);
      if (stateRef.current.writeOpen || stateRef.current.describeOpen) {
        setMergedState({ writeOpen: false, describeOpen: false });
      }
    };
    editorContainerRef.current?.addEventListener("mousedown", handleEditorMousedownRef.current);

    // Reset state
    setMergedState(getSetupEditorStateReset());

    cardQueriesRef.current = {};
    submittedRef.current = false;

    considerationRef.current = null;
    startRef.current = null;

    // set up favoriting
    favoritesStoreRef.current = new FavoritesStore(getUserId(), documentIdRef.current);
    favoritesStoreRef.current.initialize().then(() => {
      if (favoritesStoreRef.current) {
        setMergedState({
          favorites: favoritesStoreRef.current.getSortedFavorites(),
          favoritesByCards: favoritesStoreRef.current.getFavoritesByCards(),
        });
      }
    });

    // Get the card sets
    getCards(stride);

    updateWordCount();
  });

  // used to check if the user is viewing the StoryBible section of the view
  // this is used to determine if we should scroll to the cursor position when resizing the center column
  const isUserInStoryBible = useStableCallback(() => {
    const activeElement = document.activeElement;

    return getIsUserInStoryBible({
      activeElement,
      editorEndRect: editorEndRef?.current?.getBoundingClientRect(),
      centerColumnRect: centerColumnRef?.current?.getBoundingClientRect(),
    });
  });

  // Helper method preserves scroll position in document when resizing center column
  const preserveScrollPosition = useStableCallback((currentScrollTop, cursorPosition) => {
    if (shouldSuppressEntityNavigationScrollReset()) {
      return;
    }

    // Check if StoryBible inputs are active, if so we don't want to re-scroll to the cursor in the editor
    const isStoryBibleActive = isUserInStoryBible();

    // After the resize is applied and DOM is updated, restore the scroll position
    // Use requestAnimationFrame to ensure DOM updates have been processed
    requestAnimationFrame(() => {
      // First approach: directly restore scroll position if we have it
      if (currentScrollTop !== undefined && centerColumnRef.current) {
        centerColumnRef.current.scrollTop = currentScrollTop;
      }

      // When Feedback comments are open, active-card sync owns the manuscript
      // scroll position. Re-centering to the editor cursor during resize can
      // yank the reader back to an old cursor/selection while they scroll.
      if (propsRef.current.showComments) {
        return;
      }

      // Second approach: if we have a cursor position, scroll to keep it visible
      // only apply the scrolling if the editor is focused and we're not in StoryBible or settings panel
      if (!isStoryBibleActive && !propsRef.current.sudowriteStore.settingsOpen && cursorPosition !== null && tiptapRef.current && tiptapRef.current.view) {
        // Get the coordinates of the cursor position
        const coords = tiptapRef.current.view.coordsAtPos(cursorPosition);

        // Check if the cursor is visible in the viewport
        const editorContainer = window.document.getElementById("editorContainer");
        if (editorContainer && centerColumnRef.current) {
          const containerRect = centerColumnRef.current.getBoundingClientRect();

          // Calculate position relative to the editor container
          const offsetTop = coords.top + editorContainer.offsetTop;

          // If the cursor would be outside the visible area, scroll to make it visible
          if (offsetTop < containerRect.top || offsetTop > containerRect.bottom) {
            // Scroll to position the cursor in the middle of the viewport
            const scrollTarget = offsetTop - containerRect.height / 2;
            centerColumnRef.current.scrollTop = scrollTarget;
          }
        }
      }
    });
  });

  // Handler for resize events (triggered when sidebars are collapsed/expanded)
  const onResizePreserveScroll = useStableCallback(() => {
    // Store current scroll position before resize
    const currentScrollTop = centerColumnRef.current?.scrollTop;

    // Store current cursor position if available
    let cursorPosition = null;
    if (tiptapRef.current && tiptapRef.current.view && tiptapRef.current.state.selection) {
      cursorPosition = tiptapRef.current.state.selection.from;
    }

    if (!cursorPosition) {
      cursorPosition = lastKnownCursorPositionRef.current;
    }

    preserveScrollPosition(currentScrollTop, cursorPosition);
  });

  const resizeCenterColumn = useStableCallback(() => {
    // Store current scroll position before resize
    const currentScrollTop = centerColumnRef.current?.scrollTop;

    // Store current cursor position if available
    let cursorPosition = null;
    if (tiptapRef.current && tiptapRef.current.view && tiptapRef.current.state.selection) {
      cursorPosition = tiptapRef.current.state.selection.from;
      lastKnownCursorPositionRef.current = cursorPosition; // update last known cursor position
    } else {
      cursorPosition = lastKnownCursorPositionRef.current;
    }

    // Perform the resize operation
    const { columnWidths, setColumnWidths } = propsRef.current.projectStore;
    const nextLayout = getCenterColumnResizeState({
      columnWidths,
      viewportWidth: window.innerWidth,
    });

    setColumnWidths((draft) => {
      draft.center.current = nextLayout.centerCurrent;
      draft.hasResizedCenter = nextLayout.hasResizedCenter;
    });

    preserveScrollPosition(currentScrollTop, cursorPosition);
  });

  const resizeColumns = useStableCallback(() => {
    const { columnWidths, setColumnWidths } = propsRef.current.projectStore;
    const nextLayout = getColumnResizeState({
      columnWidths,
      viewportWidth: window.innerWidth,
    });

    setColumnWidths((draft) => {
      draft.left.current = nextLayout.leftCurrent;
      draft.center.current = nextLayout.centerCurrent;
      draft.right.current = nextLayout.rightCurrent;
      draft.right.collapsed = nextLayout.rightCollapsed;
      draft.hasResizedColumns = nextLayout.hasResizedColumns;
    });
  });

  const onWindowResize = useStableCallback(() => {
    const { columnWidths, setColumnWidths } = propsRef.current.projectStore;
    const nextLayout = getWindowResizeState({
      columnWidths,
      viewportWidth: window.innerWidth,
    });

    if (!nextLayout.shouldCollapsePanels) {
      setColumnWidths((draft) => {
        draft.center.current = nextLayout.centerCurrent;
      });
    } else {
      setColumnWidths((draft) => {
        draft.left.collapsed = nextLayout.leftCollapsed;
        draft.left.saved = nextLayout.leftSaved;
        draft.left.current = nextLayout.leftCurrent;

        draft.right.collapsed = nextLayout.rightCollapsed;
        draft.right.saved = nextLayout.rightSaved;
        draft.right.current = nextLayout.rightCurrent;

        draft.hasResizedColumns = nextLayout.hasResizedColumns;

        draft.center.current = nextLayout.centerCurrent;
      });
    }
  });

  React.useEffect(() => {
    useProjectStore.getState().setColumnLayoutResizeHooks({
      resizeCenterColumn,
      editorContainer: editorContainerRef.current,
    });
    return () => {
      useProjectStore.getState().setColumnLayoutResizeHooks({
        resizeCenterColumn: () => {},
        editorContainer: null,
      });
    };
  }, [resizeCenterColumn]);

  const handleScroll = useStableCallback((e) => {
    const projectStore = useProjectStore.getState();
    if (projectStore.userScrolled) {
      return;
    }

    if (stateRef.current.shouldAutoScroll) {
      setMergedState({ shouldAutoScroll: false });
    }

    projectStore.incrementScrollCount();

    const isAtBottom = isProgrammaticScrollAtBottom({
      storyBibleActive: propsRef.current.storyBibleStore.isActive,
      editorSectionElement: propsRef.current.editorSectionRef?.current,
      viewportHeight: window.innerHeight,
    });

    if (!isAtBottom && projectStore.getScrollCount() > 15) {
      projectStore.setUserScrolled(true);
    }
  });

  const handleAutoScroll = useStableCallback(async () => {
    // On first synced, we set the selection to the last known selecitonIndex on backend
    try {
      // During agent-driven remote edits we preserve user scroll manually; skip auto-scroll nudges.
      if (Date.now() <= (agentEditScrollPreserveUntilRef.current ?? 0)) {
        return;
      }
      const tiptap = tiptapRef.current;
      if (!tiptap || tiptap.isDestroyed || !tiptap.view?.docView) {
        return;
      }
      const tr = tiptap.state.tr;
      let scrollOffsetTop;

      const docSize = tiptap.state.doc.content.size;
      const selectionIndex = documentModelRef.current.selectionIndex;
      if (Number.isInteger(selectionIndex) && selectionIndex > 0 && selectionIndex <= docSize) {
        const newSelection = TextSelection.create(tiptap.state.doc, selectionIndex);
        tr.setSelection(newSelection);
        try {
          ({ top: scrollOffsetTop } = tiptap.view.coordsAtPos(selectionIndex));
        } catch {
          return;
        }
      }

      tr.scrollIntoView();
      tiptapRef.current.view.dispatch(tr);
      tiptapRef.current.commands.scrollIntoView();

      const shouldFocus = !propsRef.current.isEditing && !propsRef.current.projectStore.isEditingTextarea && !propsRef.current.isEditingDocumentTitle;

      // The tiptap editor is not a scroll container. Its parent centerColumn is
      // the scroll container. We need to adjust scroll on the parent container
      // to bring the cursor into view.
      if (scrollOffsetTop && centerColumnRef.current) {
        // We have to account for additional elements in the centerColumn.
        // So we adjust the offset to start at the offset of the editor container
        const editorContainer = window.document.getElementById("editorContainer");
        scrollOffsetTop += editorContainer.offsetTop;

        // Center the cursor based on height of the tiptap editor
        // NOTE: pre-existing NaN (`.height` is undefined on the div; it was the
        // iframe height attr string before) — kept so auto-scroll lands the same.
        scrollOffsetTop -= editorContainer.height / 2;
        if (stateRef.current.shouldAutoScroll && shouldFocus) {
          setTimeout(() => {
            centerColumnRef.current?.scrollBy({ top: scrollOffsetTop });
          }, 500);
        }
      }

      // only force focus if user is not currently editing a title
      // and if a StoryBible textarea is not in focus
      if (shouldFocus) {
        tiptapRef.current.view.focus();
      }
      if (stateRef.current.shouldAutoScroll) {
        setMergedState({ shouldAutoScroll: false });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  });

  const initializeContainerAndEditor = useStableCallback(() => {
    if (!editorContainerRef.current || !editorContainerRef.current.isConnected) {
      // TODO: use a ref instead of reaching in via id
      editorContainerRef.current = window.document.getElementById("editorContainer");
    }

    if (!editorContainerRef.current) {
      return false;
    }

    // The container is a React-rendered div, present at commit — no load event.
    tryStartSetupEditor();

    return true;
  });

  const retryInitializeContainer = useStableCallback(() => {
    if (componentUnmountedRef.current || setupEditorStartedRef.current) {
      return;
    }

    if (initializeContainerAndEditor()) {
      containerLookupRetriesRef.current = 0;
      return;
    }

    containerLookupRetriesRef.current += 1;
    if (containerLookupRetriesRef.current > 20) {
      Sentry.captureMessage("Tiptap editor container was not found during mount", { level: "warning" });
      return;
    }

    containerLookupRetryTimeoutRef.current = window.setTimeout(retryInitializeContainer, 50);
  });

  const tryStartSetupEditor = useStableCallback(() => {
    if (componentUnmountedRef.current || setupEditorStartedRef.current) {
      return false;
    }

    // `#editor` must be in the DOM before setup. Starting earlier skips
    // `append()` (null ref), leaves the ProseMirror view detached (blank
    // canvas), and `setEditable(false)` never clears until a full remount.
    if (!window.document.getElementById("editor")) {
      return false;
    }

    if (!propsRef.current.document) {
      return false;
    }

    setupEditorStartedRef.current = true;
    editorContainerRef.current.removeEventListener("click", handleEditorClickRef.current);
    editorContainerRef.current.addEventListener("click", handleEditorClickRef.current);
    setupEditor().catch((error) => {
      setupEditorStartedRef.current = false;
      Sentry.captureException(error, { tags: { feature: "tiptap_setup_editor" } });
    });

    return true;
  });

  const mountTiptapDocument = useStableCallback(() => {
    componentUnmountedRef.current = false;
    sendAppEvent("Load Editor");

    // Store reference to handler for cleanup
    handleEditorClickRef.current = () => {
      const event = new CustomEvent("editor-click");
      document.dispatchEvent(event);
    };

    initializeContainerAndEditor() || retryInitializeContainer();
    tryStartSetupEditor();

    window.addEventListener("resize", onWindowResize);
    window.addEventListener("resize", onResizePreserveScroll);

    innerRef?.current?.addEventListener("scroll", handleScroll, true);
  });

  const runTiptapDocumentUpdateEffects = useStableCallback(async (prevProps, prevState) => {
    if (
      propsRef.current.onWordCountsChange &&
      (prevState.wordCount !== stateRef.current.wordCount || prevState.highlightedWordCount !== stateRef.current.highlightedWordCount)
    ) {
      propsRef.current.onWordCountsChange({
        wordCount: stateRef.current.wordCount,
        highlightedWordCount: stateRef.current.highlightedWordCount || 0,
      });
    }

    if (!prevProps.document && propsRef.current.document) {
      tryStartSetupEditor();
    }

    if (prevProps.document?.id !== propsRef.current.document?.id && propsRef.current.document) {
      cleanupForDocumentSwitch();
      await reinitializeForDocument();
      return;
    }

    if (!propsRef.current.automaticGuidances && prevProps.automaticGuidances) {
      // if automatic guidnaces are turned off, we abort any in-flight requests
      if (guidanceAbortControllerRef.current) {
        guidanceAbortControllerRef.current.abort();
      }
    }

    if (propsRef.current.projectStore?.columnWidths?.hasResizedCenter && !propsRef.current.projectStore?.columnWidths?.hasResizedColumns) {
      {
        resizeColumns();
      }
    }

    // close/open blank document CTAs to sync with quick edit/chat opening
    const quickEditUpdate = getQuickEditCTAUpdates(
      prevState,
      stateRef.current,
      () => tiptapRef.current?.getText?.()
    );
    if (quickEditUpdate !== null) {
      propsRef.current.projectStore.setShowNewDocumentCTAs(quickEditUpdate);
    }

    // Restore blank document CTAs when Commander closes.
    // Commander unmounts when commandModeState transitions to "off",
    // so its own useEffect cannot observe this transition.
    const commanderUpdate = getCommanderCTAUpdate(
      prevState,
      stateRef.current,
      () => tiptapRef.current?.getText?.()
    );
    if (commanderUpdate !== null) {
      propsRef.current.projectStore.setShowNewDocumentCTAs(commanderUpdate);
    }
    // Auto scroll to previous position after editor and document are ready if user has not interacted with the editor
    if (stateRef.current.editorReady && stateRef.current.documentReady && stateRef.current.shouldAutoScroll && !propsRef.current.sudowriteStore.settingsOpen) {
      handleAutoScroll();
    }
  });

  const pendingChanges = useStableCallback(() => {
    return propsRef.current.saved === "saving";
  });

  const handleChange = useStableCallback((event) => {
    var update = {};
    update[event.target.name] = event.target.value;
    setMergedState(update);
  });

  const documentPluginBridge = React.useMemo(
    () => ({
      documentId: () => documentIdRef.current,
      highlighted_text: () => getSelection().text,
      errorHandlers: {
        show_error: (msg) => showError(msg),
      },
    }),
    [getSelection, showError],
  );

  const pluginOpenWormhole = useStableCallback(({ guidance = "", sessionId = "" } = {}) => {
    const pluginWriteMode = propsRef.current.writeMode.value === "guided" ? "guided" : "auto"; // Use auto for tone mode
    const writePlugins = propsRef.current.sudowriteStore.installedPlugins

      .map((s) => {
        const [, type, mode, model] = s.plugin.id.split("/");
        return {
          plugin: s.plugin,
          mode,
          model,
          type,
        };
      })
      .filter((s) => (s.type === "write" || s.type === "writeClaude3") && s.mode === pluginWriteMode && s.model === resolveBenchmarkWriteModel(selectedWriteModel));
    const plugin = writePlugins[0]?.plugin;
    if (!plugin) return;
    const inputDocumentBlock = plugin.blocks.find((block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT);
    if (!inputDocumentBlock) return;
    const params = openWormhole({ constructOnly: true, guidance, sessionId, considerationLength: 20000 });
    runDocumentPluginAction(inputDocumentBlock, plugin, documentPluginBridge, true, params, undefined, CrossAppSignal.WRITE_RUNNING);
  });

  const runDocumentPluginAction = useStableCallback(async (
    inputDocumentBlock,
    plugin,
    documentPluginBridge,
    dynamic = false,
    userParams = {},
    nonce,
    crossAppSignal = undefined,
    selectionOverride = null,
  ) => {
    // This method doesn't invoke a plugin, but creates data that will be used to run the plugin
    // elsewhere. The blocks in the plugin config are processed to create a single object describing
    // the transformation, context, and model to be used.
    try {
      const selection = selectionOverride || getSelection();
      const effectiveDocumentPluginBridge = selectionOverride
        ? { ...documentPluginBridge, highlighted_text: () => selection.text }
        : documentPluginBridge;
      let highlightedText = "";
      let precedingText = "";

      const params = inputDocumentBlock.outputs.reduce((acc, output) => {
        const configuredOutputOptions = inputDocumentBlock.options.outputs.find((o) => o.target === output.name);
        if (!configuredOutputOptions) {
          acc[output.name] = effectiveDocumentPluginBridge[output.type]();
          return acc;
        }
        if (configuredOutputOptions.source === "prompt_builder") {
          // no-op, because it must be manually calculated after the rest are figured out.
        }
        if (configuredOutputOptions.source === "highlighted_text") {
          const content = selection.text;
          const length = countWords(content);
          if (length < configuredOutputOptions.minWords) {
            throw new PluginException(`Select at least ${configuredOutputOptions.minWords} words.`);
          }
          if (length > configuredOutputOptions.maxWords) {
            throw new PluginException(`Too many words selected. Select fewer than ${configuredOutputOptions.maxWords} words.`);
          }
          highlightedText = content;
          acc[output.name] = content;
        }
        if (configuredOutputOptions.source === "preceding_text") {
          const allPrecedingText = tiptapRef.current.getText().slice(0, selection.index - 1);
          const precedingWords = splitWords(allPrecedingText);
          if (precedingWords.length < configuredOutputOptions.minWords) {
            throw new PluginException(`This action requires at least ${configuredOutputOptions.minWords} words before the cursor.`);
          }
          // // word counting is approximate.  countWords is better than splitting.
          // const usageSlice = precedingWords.slice(-configuredOutputOptions.maxWords);
          // const usage = usageSlice.join(" ");
          // const usageLength = countWords(usage);
          // const diffFromMax = usageLength - configuredOutputOptions.maxWords;
          // if (diffFromMax > 0) {
          //   // slice from the front
          //   const reslice = usageSlice.slice(diffFromMax);
          //   acc[output.name] = reslice.join(" ");
          // } else {
          //   acc[output.name] = usage;
          // }
          // precedingText = acc[output.name];

          precedingText = pluginConsideration(configuredOutputOptions.maxWords, selection);
          acc[output.name] = precedingText;
        }

        if (configuredOutputOptions.source === "following_text") {
          const followingText = pluginConsiderationForward(configuredOutputOptions.maxWords, selection);
          acc[output.name] = followingText;
        }

        return acc;
      }, {});

      const { user_text_input, ...splicedUserParams } = userParams;
      const trimmedUserTextInput = typeof user_text_input === "string" ? user_text_input.trim() : "";
      if (trimmedUserTextInput) {
        params["user_text_input"] = trimmedUserTextInput;
      }
      params.userParams = splicedUserParams;
      if (/^documentPlugins\/write/.test(plugin.id || "")) {
        const state = useProjectStore.getState();
        params.userParams.writePovTenseSnapshot = snapshotClientWritePovTense({
          capturedAt: new Date().toISOString(),
          documentId: state.document?.id,
          documentPovTenseId: state.document?.povTenseId,
          documentSettingsStatus: state.currentDocumentPOVTenseStatus,
          projectId: state.project?.id,
          isMultiplePOV: typeof state.isMultiplePOV === "boolean" ? state.isMultiplePOV : state.isMultiplePOV?.value,
          saving: state._povTenseSaving,
          project: snapshotPovTenseSettings(state.povTense),
          // This is the client store, not a claim that a particular control is
          // visible or that its values finished loading for the current document.
          document: snapshotPovTenseSettings(state.currentDocumentPOVTense),
        });
      }
      const textParts = [precedingText || "", highlightedText || ""].filter(Boolean);
      let cardPrompt = trimmedUserTextInput;
      if (!cardPrompt) {
        cardPrompt = textParts.length > 0 ? textParts.join(" ") : "";
      }
      params.card_prompt = cardPrompt || "";
      let cardModel = new Card(
        null,
        {
          source: "plugin",
          documentId: documentIdRef.current,
          prompt: params.card_prompt,
          modifier: plugin.name,
          createdAt: new Date(),
          content: [],
          userId: getUserId(),
          crossAppSignal: crossAppSignal,
        },
        false,
      );
      // Client-only: lets a Muse 2 Alpha comparison show the text these
      // continuations follow. Never sent to the server.
      cardModel.benchmarkPrecedingText = precedingText || "";

      // Create the placeholder empty card
      let card = {
        loading: true,
        doneStreaming: false,
        hidden: false,
        model: cardModel,
        ready: true,
        params: {
          pluginId: plugin.id,
          params,
          ...(userParams.benchmarkMode === true && { benchmarkMode: true }),
          ...(sprintRef.current.writeAvailable && /^documentPlugins\/write(?:Claude3)?\/(auto|guided)\/excellent$/.test(plugin.id) && { novelNovemberSprint: true }),
          endpointOverride: `/api/ai/${dynamic ? "runDynamicPluginAction" : "runPluginAction"}/${plugin.id}${nonce ? `?nonce=${nonce}` : ""}`,
        },
      };

      prependNewCard(card);
    } catch (error) {
      console.error("Plugin Error", error);
      if (error instanceof PluginException) {
        showError(error.message);
      } else {
        showError("There is an error in the configuration of this plugin");
      }
    }
  });

  const loadSampleStory = useStableCallback(() => {
    insertEditor({ text: SAMPLE_STORY });
  });

  const setCommandModeState = useStableCallback((cm) => {
    setMergedState({ commandModeState: cm });
  });

  const closeRelatedWords = useStableCallback(() => {
    setMergedState({ showRelatedWords: false });
  });

  const setWriteOpen = useStableCallback((bool) => {
    setMergedState({ writeOpen: bool });
  });

  const setDescribeOpen = useStableCallback((bool) => {
    setMergedState({ describeOpen: bool });
  });

  const closeGuidance = useStableCallback(() => {
    setMergedState({ guidanceOpen: false });
  });

  const openGuidance = useStableCallback(() => {
    setMergedState({ guidanceOpen: true });
  });

  const setGuidances = useStableCallback((val) => {
    setMergedState({ guidances: val });
  });

  const openAgenticChatFromHelp = useStableCallback(() => {
    const currentProps = propsRef.current;
    const importBlocksRightBar = currentProps.project?.importStep && currentProps.project.importStep !== "CANCELLED";
    if (currentProps.columnWidths?.right?.collapsed && !importBlocksRightBar) {
      currentProps.setRightBarCollapsed(false);
    }
    currentProps.emitSudoLayoutEvent?.("focus_chat", { source: "help_menu_agentic" });
  });

  const closeBrainstorm = useStableCallback(() => {
    setMergedState({ brainstormOpen: false });
    refreshCards();
  });

  const setFocus = useStableCallback((val) => {
    setMergedState({ focus: val });
  });

  const setShowThumbs = useStableCallback((val) => {
    setMergedState({ showThumbs: val });
  });

  const documentId = props.documentId || props.document?.id;
  const installedPlugins = props.sudowriteStore.installedPlugins;

  const relatedWordsProps = React.useMemo(
    () => ({
      showRelatedWords: state.showRelatedWords,
      documentId,
      relatedWordsStore: relatedWordsStoreRef.current,
      insertRelatedWord,
      close: closeRelatedWords,
    }),
    [state.showRelatedWords, insertRelatedWord, closeRelatedWords, documentId],
  );

  const commandModeProps = React.useMemo(
    () => ({
      loadingDocument: loadingDocumentRef.current,
      commandModeState: state.commandModeState,
      wordCount: state.wordCount,
      tiptapTextLength: tiptapRef.current?.getText()?.length,
      subscription: props.subscription,
      leftBarCollapsed: props.leftBarCollapsed,
      editorOptions: props.editorOptions,
      commandModeInserting: state.commandModeInserting,
      setCommandModeState,
      handleCommandMode,
      editorSectionRef: props.editorSectionRef,
      caretPosition: state.caretPosition,
      magicCursorVisible: state.magicCursorVisible,
      editorContainer: editorContainerRef.current,
      theme: props.theme,
    }),
    [
      state.commandModeState,
      state.wordCount,
      state.commandModeInserting,
      state.caretPosition,
      state.magicCursorVisible,
      props.subscription,
      props.leftBarCollapsed,
      props.editorOptions,
      props.editorSectionRef,
      props.theme,
      setCommandModeState,
      handleCommandMode,
    ],
  );

  const menuProps = React.useMemo(
    () => ({
      project: props.project,
      menuRef: props.menuRef,
      menuComponentRef: menuRef,
      openWormhole,
      describeSenses: props.describeSenses,
      setDescribeSenses: props.setDescribeSenses,
      memory: props.memory,
      setMemory: props.setMemory,
      memoryEnabled: props.memoryEnabled,
      setMemoryEnabled: props.setMemoryEnabled,
      writeMode: props.writeMode,
      writeOpen: state.writeOpen,
      setWriteOpen,
      describeOpen: state.describeOpen,
      setDescribeOpen,
      setWriteMode: props.setWriteMode,
      tone: props.tone,
      setTone: props.setTone,
      guidances: state.guidances,
      guidanceOpen: state.guidanceOpen,
      closeGuidance,
      openGuidance,
      loadingGuidances: state.loadingGuidances,
      fetchGuidance,
      automaticGuidances: props.automaticGuidances,
      setAutomaticGuidances: props.setAutomaticGuidances,
      setGuidances,
      documentId,
      subscription: props.subscription,
      highlightDescription: state.highlightDescription,
      openDescription,
      summaryEnabled: props.summaryEnabled,
      filterEnabled: props.filterEnabled,
      openSummary: undefined,
      openFeedback,
      openExpand,
      openVisualize,
      openFilter,
      openCustomFunction,
      openBrainstorm: handleOpenBrainstorm,
      labsOpen: state.labsOpen,
      setLabsOpen,
      storyToolOpen: state.storyToolOpen,
      setStoryToolOpen,
      toneOpen: state.toneOpen,
      setToneOpen,
      wordCount: state.wordCount,
      highlightedWordCount: state.highlightedWordCount,
      editorOptions: props.editorOptions,
      saved: props.saved,
      setEditorOptions: props.setEditorOptions,
      wormholeOutputLength: props.wormholeOutputLength,
      wormholeOutputCards: props.wormholeOutputCards,
      creativitySliderPosition: props.creativitySliderPosition,
      openAdmin: props.openAdmin,
      pluginSchemas: installedPlugins,
      runDocumentPluginAction,
      documentPluginBridge,
      showFront: props.showFront,
      openAgenticChatFromHelp,
      setCommandModeState,
      commandModeState: state.commandModeState,
      showFirstDraft: state.wordCount === 0,
      writeModel: props.writeModel,
      trashedItem: props.trashedItem,
      modalOpen: state.modalOpen,
      setModalOpen,
      getDocumentJSON,
    }),
    [
      props.project,
      props.menuRef,
      openWormhole,
      props.describeSenses,
      props.setDescribeSenses,
      props.memory,
      props.setMemory,
      props.memoryEnabled,
      props.setMemoryEnabled,
      props.writeMode,
      state.writeOpen,
      setWriteOpen,
      state.describeOpen,
      setDescribeOpen,
      props.setWriteMode,
      props.tone,
      props.setTone,
      state.guidances,
      state.guidanceOpen,
      closeGuidance,
      openGuidance,
      state.loadingGuidances,
      fetchGuidance,
      props.automaticGuidances,
      props.setAutomaticGuidances,
      setGuidances,
      documentId,
      props.subscription,
      state.highlightDescription,
      openDescription,
      props.summaryEnabled,
      props.filterEnabled,
      openFeedback,
      openExpand,
      openVisualize,
      openFilter,
      openCustomFunction,
      handleOpenBrainstorm,
      state.labsOpen,
      setLabsOpen,
      state.storyToolOpen,
      setStoryToolOpen,
      state.toneOpen,
      setToneOpen,
      state.wordCount,
      state.highlightedWordCount,
      props.editorOptions,
      props.saved,
      props.setEditorOptions,
      props.wormholeOutputLength,
      props.wormholeOutputCards,
      props.creativitySliderPosition,
      props.openAdmin,
      installedPlugins,
      runDocumentPluginAction,
      documentPluginBridge,
      props.showFront,
      openAgenticChatFromHelp,
      setCommandModeState,
      state.commandModeState,
      props.writeModel,
      props.trashedItem,
      state.modalOpen,
      setModalOpen,
      getDocumentJSON,
    ],
  );

  const brainstormProps = React.useMemo(
    () => ({
      brainstormOpen: state.brainstormOpen,
      documentId,
      closeBrainstorm,
      seenTips: state.seenTips,
      setSeenTips,
    }),
    [state.brainstormOpen, state.seenTips, closeBrainstorm, setSeenTips, documentId],
  );

  const editorDocumentProps = React.useMemo(
    () => ({
      trashedItem: props.trashedItem,
      editorName: props.tiptapDocumentSBEditorName || "TiptapDocumentSB",
      documentId,
      userId: props.user?.uid,
      projectId: props.project?.id,
      leftBarCollapsed: props.leftBarCollapsed,
      height: props.height,
      range: state.range,
      focus: state.focus,
      setFocus,
      editor: tiptapRef.current,
      editorReady: state.editorReady && editorModelRef.current?.documentId === documentId,
      insertEditor,
      disabled: state.commandModeInserting || props.storyBibleStore.isProseStreaming,
      editorComputedStyles,
      project: props.project,
      contents: props.contents,
      centerColumnRef,
      editorToolbarRef,
      editorRef,
      editorEndRef,
      showEditableTitle: state.commandModeState === "off",
      showThumbs: state.showThumbs,
      setShowThumbs,
      toggleFindAndReplaceOpen,
      titleEditorRef,
      user: props.user,
      setRightBarCollapsed: setRightBarCollapsedWrapper,
      hideLinkingMenu,
      inTrashView: props.trashedItem || props.deleted,
      ydoc: editorModelRef.current?.ydoc,
      existingCards: state.existingCards,
      existingCardsLoaded: state.existingCardsLoaded,
      onUndo: handleEditorUndo,
      onRedo: handleEditorRedo,
      toggleSpellCheck,
      addDraftPlaceholderCard,
      appendDraftPlaceholderContent,
      linkDraftPlaceholderToCardId,
      finalizeDraftPlaceholder,
      removeDraftPlaceholderCard,
    }),
    [
      props.trashedItem,
      props.tiptapDocumentSBEditorName,
      documentId,
      props.leftBarCollapsed,
      props.height,
      state.range,
      state.focus,
      state.editorReady,
      setFocus,
      insertEditor,
      state.commandModeInserting,
      props.storyBibleStore.isProseStreaming,
      editorComputedStyles,
      props.project,
      props.contents,
      state.commandModeState,
      state.showThumbs,
      setShowThumbs,
      toggleFindAndReplaceOpen,
      props.user,
      setRightBarCollapsedWrapper,
      hideLinkingMenu,
      props.deleted,
      state.existingCards,
      state.existingCardsLoaded,
      handleEditorUndo,
      handleEditorRedo,
      toggleSpellCheck,
      addDraftPlaceholderCard,
      appendDraftPlaceholderContent,
      linkDraftPlaceholderToCardId,
      finalizeDraftPlaceholder,
      removeDraftPlaceholderCard,
    ],
  );

  const newDocumentCtaProps = React.useMemo(
    () => ({
      subscription: props.subscription,
      showNewDocumentCTAs: props.projectStore.showNewDocumentCTAs,
      deleted: props.deleted,
      trashedItem: props.trashedItem,
      proseGeneratorActive: props.storyBibleStore.proseGeneratorActive,
      editorOptions: props.editorOptions,
      openWormhole,
      runDocumentPluginAction,
      documentPluginBridge,
    }),
    [
      props.subscription,
      props.projectStore.showNewDocumentCTAs,
      props.deleted,
      props.trashedItem,
      props.storyBibleStore.proseGeneratorActive,
      props.editorOptions,
      openWormhole,
      runDocumentPluginAction,
      documentPluginBridge,
    ],
  );

  const renderTiptapDocument = () => {
    const currentProps = props;
    const currentState = state;
    const {
      notFound,
      loadError,
      brainstormOpen,
      editorActive,
      tone,
      toneOpen,
      storyToolOpen,
      labsOpen,
      showRelatedWords,
      selectionRange,
      wordCount,
      highlightedWordCount,
      highlightDescription,
      forceNeedsFeedback,
      highlightInsert,
      rightTab,
      newCards,
      existingCards,
      existingCardsLoaded,
      favorites,
      favoritesByCards,
      message,
      error,
      modalOpen,
    } = currentState;

    const { editorOptions, setEditorOptions, subscription, needsFeedback, updateNeedsFeedback, updateHideEDUCards, totalDocuments, summaryEnabled, filterEnabled } = currentProps;
    const pluginSchemas = currentProps.sudowriteStore.installedPlugins;
    const isSettingsOpen = currentProps.sudowriteStore.settingsOpen;

    if (notFound) {
      return (
        <PageNotFound
          disableHeader={true}
          message="This doc does not exist."
        />
      );
    }

    if (loadError) {
      return (
        <div id="multiverse">
          <div className="load-error">
            <h1>{loadError}</h1>
          </div>
        </div>
      );
    }

    // Apply styles in the editor
    // Note: text-indent is handled via CSS class (.indented) rather than inline style
    // to prevent visual "jumping" during re-renders in contenteditable contexts
    applyEditorComputedStyles(editorRef.current, editorComputedStyles);
    // Theme attributes live on the parent <body> (lib/Theme.js); the editor is
    // in the main document now, so no mirroring is needed.
    const editorNode = editorRef.current?.isConnected ? editorRef.current : null;
    const selectionBounds =
      tiptapRef.current && editorNode ? getSelectedTextPixelPosition(tiptapRef.current, editorNode) : null;

    const showBrainstorm = brainstormOpen;

    // Whether there is an overlay open above the editor
    const isOverlay = currentState.focus || showBrainstorm;

    const importEnrichmentInProgress =
      currentProps.project?.importStep && currentProps.project.importStep !== "CANCELLED";

    const showHighlightMenu = shouldShowHighlightMenu({
      subscription,
      state: currentState,
      selectionBounds,
      selectionRange,
      hasEditorContainer: editorContainerRef.current,
      tiptapViewHasFocus: tiptapRef.current?.view.hasFocus(),
      isPointerDragging,
    });

    const actuallyShowCommandMenu =
      currentState.showCommandMenu && //
      editorContainerRef.current &&
      editorModelRef.current &&
      tiptapRef.current &&
      !tiptapRef.current.isDestroyed;

    return (
      <StyledContainer className="tiptapdocumentsb-component-container">
        {/* <Prompt
          when={pendingChanges()}
          message={(location) => `There may be pending changes to this doc. Are you sure you want to navigate away?`}
        /> */}

        <DocumentStatusBanners
          message={message}
          error={error}
          deleted={currentProps.deleted}
          trashedItem={currentProps.trashedItem}
          restoreDocument={currentProps.restoreDocument}
          restoreTrashedItem={restoreTrashedItemFromBanner}
        />

        {/* Keep the class component's overlay sibling order before #multiverse. */}
        {actuallyShowCommandMenu && (
          <CommandMenu
            subscription={subscription}
            editor={editorModelRef.current}
            selectionBounds={selectionBounds}
            fontSize={editorOptions.fontSize}
            node={editorNode}
            range={selectionRange}
            tiptap={tiptapRef.current}
            projectId={currentProps.project.id}
            didFinalizeChangesRef={commandMenuDidFinalizeChangesRef}
            dismiss={dismissCommandMenu}
            showError={(msg) => showError(msg)}
          />
        )}

        <FeedbackModal
          open={currentProps.projectStore.feedbackOpen}
          feedbackInvocation={currentProps.projectStore.feedbackInvocation}
          close={() => currentProps.projectStore.setFeedbackOpen(false)}
          setRecentInvocations={currentProps.storyBibleStore.setRecentInvocations}
          recentInvocations={currentProps.storyBibleStore.recentInvocations}
          projectId={currentProps.project.id}
        />

        {!actuallyShowCommandMenu && showHighlightMenu && !currentState.commandModeInserting && !showRelatedWords && (
          <HighlightMenu
            describeSelection={currentState.describeSelection && subscription.hasAccessToFeatures}
            commandMenuSelection={currentState.commandMenuSelection && subscription.hasAccessToQuickEdit}
            expandSelection={currentState.expandSelection && subscription.hasAccessToFeatures}
            rewriteSelection={currentState.rewriteSelection && subscription.hasAccessToFeatures}
            openRelatedWords={openRelatedWords}
            openFilter={openFilter}
            openDescription={openDescription}
            openExpand={openExpand}
            openMyVoiceFunctions={currentState.highlightedWordCount <= 4000 ? openMyVoiceFunctions : null}
            toggleCommandMenu={toggleCommandMenu}
            showMore={!currentState.focus}
            insertRelatedWord={insertRelatedWord}
            clusters={currentState.relatedClusters}
            selectionBounds={selectionBounds}
            fontSize={editorOptions.fontSize}
            editorContainer={editorContainerRef.current}
            node={editorNode}
            portalNode={editorNode}
            range={selectionRange}
            allTextHighlighted={selectionRange && tiptapRef.current?.view.state.doc.content.size === selectionRange.length}
            setRightBarCollapsed={setRightBarCollapsedWrapper}
            editor={tiptapRef.current}
            pointerStartPos={pointerDownPosition}
            pointerEndPos={pointerUpPosition}
            // The function editor can lose this menu portal before click; mousedown keeps highlight-menu actions reliable.
            activateActionsOnMouseDown
          />
        )}

        <div id="multiverse">
          <div
            ref={innerRef}
            className="inner"
          >
            <div
              className="multiverse-resizable-container"
              style={{
                height: "100%",
                "--center-column-width": `${
                  currentProps.projectStore.columnWidths.center.current ??
                  window.innerWidth -
                    currentProps.projectStore.columnWidths.left.current -
                    currentProps.projectStore.columnWidths.right.current
                }px`,
              }}
            >
              <ResizeHandle
                side="left"
                placement="between"
                editorContainer={editorContainerRef.current}
                resizeCenterColumn={resizeCenterColumn}
              />

              <div
                className="resizable-container-with-handles"
                onLoad={resizeCenterColumn}
                style={{
                  width: currentProps.projectStore.columnWidths.center.current,
                  minWidth: currentProps.projectStore.columnWidths.center.min,
                  flexGrow: 0,
                }}
                ref={outerScrollRef}
              >
                <div
                  className={classnames({
                    "editor-stack": true,
                    sw2: true,
                    "full-height": !currentProps.storyBibleStore.isActive || importEnrichmentInProgress,
                    "styled-scrollbars": !currentProps.appStore.isMac,
                  })}
                  ref={centerColumnRef}
                >
                  <TiptapSearchableRegistrar
                    editor={editorModelRef.current}
                    scrollHostRef={centerColumnRef}
                    editorHostNode={editorNode}
                    onOpenFindReplace={() => {
                      if (!currentState.findAndReplaceOpen) {
                        setFindAndReplaceOpen(true);
                      }
                    }}
                  />

                  {currentState.findAndReplaceOpen && !isSettingsOpen && (
                    <FindAndReplace
                      theme={currentProps.theme}
                      editor={editorModelRef.current}
                      documentId={documentIdRef.current}
                      columnWidths={currentProps.projectStore.columnWidths}
                      editorHostNode={editorNode}
                      scrollHostRef={centerColumnRef}
                      close={() => {
                        setFindAndReplaceOpen(false);
                      }}
                    />
                  )}

                  <Section
                    id="editor-section"
                    className={classnames({
                      "section-component-container left-bar-collapsed": currentProps.leftBarCollapsed && !isOverlay,
                      overlay: isOverlay,
                      sw2: true,
                      "full-height": !currentProps.storyBibleStore.isActive  || importEnrichmentInProgress,
                      "hidden-trash": currentProps.trashedItem,
                      "chapter-glue-beta": subscription?.hasAccessToChapterGlueBeta,
                    })}
                    innerRef={currentProps.editorSectionRef}
                    onClick={(event) => {
                      const tiptap = editorModelRef.current?.tiptap;

                      if (!tiptap) {
                        return;
                      }
                      if (!tiptap.isFocused && event.currentTarget === event.target) {
                        tiptap.commands.focus("end");
                      }
                    }}
                  >
                    <EditorContentPanels
                      relatedWordsProps={relatedWordsProps}
                      commandModeProps={commandModeProps}
                      menuProps={menuProps}
                      brainstormProps={brainstormProps}
                      editorDocumentProps={editorDocumentProps}
                      newDocumentCtaProps={newDocumentCtaProps}
                    />
                  </Section>

                  {!currentProps.deleted && !currentProps.trashedItem && !importEnrichmentInProgress && (
                    <Section
                      className="story-bible"
                      style={{ marginTop: currentProps.storyBibleStore.isActive ? 20 : 0, overflow: "visible" }}
                    >
                      <StoryBible
                        subscriptionJSON={subscription}
                        userData={currentProps.userData}
                        user={currentProps.user}
                        projectId={currentProps.project.id}
                        project={currentProps.project}
                        documentId={documentIdRef.current}
                        documentLength={getStoryBibleDocumentLengthForContentState(
                          tiptapRef.current?.view?.state?.doc?.content?.size
                        )}
                        editorSectionRef={currentProps.editorSectionRef}
                        setDocumentId={currentProps.setDocumentId}
                        centerColumnRef={centerColumnRef}
                        onStyleGuidePointerClick={currentProps.onStoryBibleStylePointerClick}
                        onStyleGuideStyleSave={currentProps.onStoryBibleStyleSave}
                        styleGuideDisplayStyle={currentProps.styleGuideDisplayStyle}
                        selectedStyleSource={currentProps.selectedStyleSource}
                        selectedStyleName={currentProps.selectedStyleName}
                      />
                    </Section>
                  )}
                </div>
              </div>

              <ResizeHandle
                side="right"
                placement="between"
                editorContainer={editorContainerRef.current}
                resizeCenterColumn={resizeCenterColumn}
              />

              <RightBarContainer
                editor={editorModelRef.current}
                passedRef={rightBarRef}
                existingCardsLoaded={existingCardsLoaded}
                totalDocuments={totalDocuments}
                openFilter={openFilter}
                getSelection={getSelection}
                signal={controllerRef.current.signal}
                needsFeedback={needsFeedback || forceNeedsFeedback}
                updateNeedsFeedback={updateNeedsFeedback}
                updateHideEDUCards={updateHideEDUCards}
                demo={false}
                highlightInsert={highlightInsert}
                tab={rightTab}
                onChangeTab={onChangeTab}
                loading={false}
                deleteCard={deleteCard}
                onDeleteCardItem={deleteCardItem}
                onCardStreamSettled={markCardStreamSettled}
                newCards={newCards}
                existingCards={existingCards}
                favorites={favorites}
                favoritesByCards={favoritesByCards}
                onNextPage={onNextPage}
                insertEditor={insertEditor}
                favoritesStore={favoritesStoreRef.current}
                onCardFavorite={onCardFavorite}
                onCardUnfavorite={onCardUnfavorite}
                onDeleteFavorite={onDeleteFavorite}
                onFavoriteReorder={onFavoriteReorder}
                openBrainstorm={openBrainstorm}
                delimited={delimited}
                memory={currentState.memory}
                setMemory={setMemory}
                showFront={currentProps.showFront}
                project={currentProps.project}
                userData={currentProps.userData}
                loadSampleStory={loadSampleStory}
                setCollapsed={setRightBarCollapsedWrapper}
                rememberModifier={(modifier) => { lastRewriteModifierRef.current = modifier; }}
              />
            </div>
          </div>
        </div>
        <BenchmarkTakeover
          userId={currentProps.user?.uid}
          documentId={documentIdRef.current}
          insertText={insertEditor}
          removeInterceptedCard={removeDraftPlaceholderCard}
          refreshCards={refreshCards}
        />
      </StyledContainer>
    );
  };

  React.useLayoutEffect(() => {
    isMountedRef.current = true;
    componentUnmountedRef.current = false;
    mountTiptapDocument();

    return () => {
      isMountedRef.current = false;
      didCommitRef.current = false;
      cleanupTiptapDocument();
    };
  }, [cleanupTiptapDocument, mountTiptapDocument]);

  React.useLayoutEffect(() => {
    if (!isMountedRef.current) {
      return;
    }

    if (!didCommitRef.current) {
      didCommitRef.current = true;
      previousPropsRef.current = props;
      previousStateRef.current = state;
      return;
    }

    const previousProps = previousPropsRef.current;
    const previousState = previousStateRef.current;

    if (previousProps !== props || previousState !== state) {
      void runTiptapDocumentUpdateEffects(previousProps, previousState);
    }

    const callbacks = pendingSetStateCallbacksRef.current.splice(0);
    callbacks.forEach((callback) => {
      callback();
    });

    previousPropsRef.current = props;
    previousStateRef.current = state;
  });

  return renderTiptapDocument();
}

const WrappedTiptapDocumentSB = React.memo(withProjectStore(withSudowriteStore(withAppStore(withStoryBibleStore(withCommentStore(withStickyTourStore(withCommentToggle(TiptapDocumentSB))))))));

// Wrap with SearchableProvider to enable global find/replace
function TiptapDocumentSBWithSearchable(props) {
  return (
    <SearchableProvider>
      <WrappedTiptapDocumentSB {...props} />
    </SearchableProvider>
  );
}

export default React.memo(TiptapDocumentSBWithSearchable);


